import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  AddressBook,
  ArrowUp,
  BookOpen,
  CalendarBlank,
  CalendarX,
  ChartBar,
  ChatsCircle,
  ChatText,
  CurrencyInr,
  DownloadSimple,
  EnvelopeSimple,
  FileText,
  GraduationCap,
  Stack,
  ImagesSquare,
  Megaphone,
  Notebook,
  Paperclip,
  PencilSimple,
  Plus,
  Printer,
  Trash,
  UserCheck,
  UserCircle,
  Users,
  Wallet,
  WhatsappLogo,
  X,
  CalendarCheck,
} from "@phosphor-icons/react";
import DashboardShell from "@/components/DashboardShell";
import { useAuth } from "@/context/AuthContext";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { LeavesTab, StaffAttendanceTab, VisitorsTab, ExamsTab, ReportsTab } from "@/pages/SchoolOps";
import { DailyReport, MonthlyReport } from "@/components/attendance/AttendanceReports";
import { MarkAttendance } from "@/components/attendance/MarkAttendance";
import { TempAccessPanel } from "@/components/attendance/TempAccess";
import { TimetableTab } from "@/pages/Timetable";
import ReportCard from "@/components/ReportCard";
import { ClassSubjects } from "@/components/ClassSubjects";
import AdminMessages from "@/components/messaging/AdminMessages";
import { SchoolMoments } from "@/components/MediaMoments";
import LearningManagement from "@/components/LearningManagement";
import StaffMaster from "@/components/StaffMaster";
import api, { API, daysAgoLocal, formatErr, fmtDate, inr, openFile, todayLocal } from "@/lib/api";
import { Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const NAV = [
  { id: "overview", label: "Overview", labelKey: "navigation.dashboard", icon: ChartBar },
  { id: "messages", label: "Communication", labelKey: "navigation.communication", icon: ChatsCircle },
  { id: "teachers", label: "Teachers", labelKey: "navigation.teachers", icon: Users },
  { id: "classes", label: "Classes", labelKey: "navigation.classes", icon: BookOpen },
  { id: "students", label: "Students", labelKey: "navigation.students", icon: GraduationCap },
  { id: "learning", label: "Learning", icon: Stack },
  { id: "attendance", label: "Attendance", labelKey: "navigation.attendance", icon: CalendarCheck },
  { id: "broadcasts", label: "Broadcasts", labelKey: "navigation.broadcasts", icon: Megaphone },
  { id: "school-moments", label: "Moments", icon: ImagesSquare },
  { id: "fees", label: "Fee Accounts", labelKey: "navigation.feeLedger", icon: Wallet },
  { id: "leaves", label: "Leaves", labelKey: "navigation.leaves", icon: CalendarX },
  { id: "staff-attendance", label: "Staff Master", icon: UserCheck },
  { id: "visitors", label: "Visitors", labelKey: "navigation.visitors", icon: AddressBook },
  { id: "exams", label: "Exams", labelKey: "navigation.exams", icon: Notebook },
  { id: "report-cards", label: "Report Cards", labelKey: "navigation.reportCards", icon: FileText },
  { id: "timetable", label: "Timetable", labelKey: "navigation.timetable", icon: CalendarBlank },
  { id: "reports", label: "Reports", labelKey: "navigation.reports", icon: FileText },
  { id: "school-profile", label: "School Profile", icon: UserCircle },
];

const FREQ_LABEL = { monthly: "mo", quarterly: "qtr", annual: "yr" };

function ReportCardsTab({ students, classes }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [classId, setClassId] = useState("");
  const [reportFor, setReportFor] = useState(null);
  const [bulkTerm, setBulkTerm] = useState("Term 1");
  const [bulkBusy, setBulkBusy] = useState(false);
  const classMap = Object.fromEntries(classes.map((c) => [c.id, c.name]));
  const filtered = students.filter(
    (s) => (!classId || s.class_id === classId) && (!search || s.name.toLowerCase().includes(search.toLowerCase()))
  );

  const downloadClassPdf = async () => {
    setBulkBusy(true);
    try {
      const res = await api.get(`/school/classes/${classId}/report-cards/export`, { params: { term: bulkTerm }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_cards_${bulkTerm.replace(" ", "")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Class report cards downloaded");
    } catch (e) {
      toast.error(formatErr(e, "Export failed"));
    } finally {
      setBulkBusy(false);
    }
  };
  return (
    <div data-testid="report-cards-tab">
      <h1 className="font-heading text-2xl font-extrabold mb-1">{t("navigation.reportCards")}</h1>
      <p className="text-sm text-slate-500 mb-6">Generate term-wise report cards from graded exams, add teacher remarks, publish to parents or download as PDF.</p>
      <div className="flex flex-wrap gap-3 mb-5">
        <Input data-testid="rc-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search student…" className="w-56 bg-white" />
        <Select value={classId || "all"} onValueChange={(v) => setClassId(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="rc-class-filter" className="w-44 bg-white"><SelectValue placeholder="All classes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("dashboard.allClasses")}</SelectItem>
            {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={bulkTerm} onValueChange={setBulkTerm}>
          <SelectTrigger data-testid="rc-bulk-term-select" className="w-32 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["Term 1", "Term 2", "Term 3", "Final"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button data-testid="rc-class-download-btn" variant="outline" disabled={!classId || bulkBusy} onClick={downloadClassPdf} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
          <FileText size={15} className="mr-1.5" /> {bulkBusy ? "Preparing…" : "Download class PDF"}
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {filtered.map((s) => (
          <button key={s.id} data-testid={`rc-student-${s.id}`} onClick={() => setReportFor(s.id)} className="w-full flex items-center justify-between px-5 py-4 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors duration-150 text-left">
            <div>
              <p className="font-semibold">{s.name}</p>
              <p className="text-xs text-slate-400">{classMap[s.class_id] || ""}{s.roll_no ? ` · Roll ${s.roll_no}` : ""}</p>
            </div>
            <span className="inline-flex items-center gap-2 text-xs font-bold text-brand-coralDark">View report card <FileText size={15} /></span>
          </button>
        ))}
        {filtered.length === 0 && <p className="text-sm text-slate-400 text-center py-10">No students match.</p>}
      </div>
      {reportFor && <ReportCard studentId={reportFor} onClose={() => setReportFor(null)} />}
    </div>
  );
}

const statusBadge = (status) => {  const map = {
    paid: "text-status-green bg-status-green/10",
    partial: "text-amber-600 bg-status-amber/20",
    due: "text-status-red bg-status-red/10",
  };
  return <span className={`text-xs font-bold px-2.5 py-1 rounded-full uppercase ${map[status] || map.due}`}>{status}</span>;
};

export default function SchoolAdmin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, returnHome } = useAuth();
  const [returningHome, setReturningHome] = useState(false);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [classFilter, setClassFilter] = useState("all");
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState("");
  const [academicYearDialogOpen, setAcademicYearDialogOpen] = useState(false);
  const [academicYearForm, setAcademicYearForm] = useState({
  name: "",
  start_date: "",
  end_date: "",
  status: "upcoming",
  is_current: false,
  });
  const [savingAcademicYear, setSavingAcademicYear] = useState(false);
  const [organizationContext, setOrganizationContext] = useState(null);
  const organizationType = organizationContext?.organization_type || "school";
  const isSchool = organizationType === "school";
  const dynamicNav = useMemo(() => NAV
    .filter((item) => isSchool || !["classes", "visitors", "exams", "report-cards", "timetable"].includes(item.id))
    .map((item) => item.id === "teachers" && !isSchool ? { ...item, label: organizationType === "phonetics" ? "Trainers" : "Faculty", labelKey: undefined } : item)
    .map((item) => item.id === "school-profile" && !isSchool ? { ...item, label: organizationType === "phonetics" ? "Centre Profile" : "Organisation Profile" } : item), [isSchool, organizationType]);
  const load = useCallback(async () => {
  try {
    const [s, t, c, b, l] = await Promise.all([
      api.get("/school/stats"),
      api.get("/school/teachers"),
      api.get("/school/classes"),
      api.get("/school/broadcasts"),
      api.get("/school/fees/ledger", {
        params: selectedAcademicYearId
          ? { academic_year_id: selectedAcademicYearId }
          : undefined,
      }),
    ]);

    setStats(s.data);
    setTeachers(t.data);
    setClasses(c.data);
    setBroadcasts(b.data);
    setLedger(l.data);
  } catch (e) {
    toast.error(formatErr(e));
  }
}, [selectedAcademicYearId]);
  const loadAcademicYears = useCallback(async () => {
    try {
      const { data } = await api.get("/school/academic-years");
      const years = Array.isArray(data) ? data : [];

      setAcademicYears(years);

      const currentYear = years.find((year) => year.is_current);
      if (currentYear) {
        setSelectedAcademicYearId(currentYear.id);
      } else if (years.length > 0) {
        setSelectedAcademicYearId(years[0].id);
      }
    } catch (e) {
      toast.error(formatErr(e, "Could not load academic years"));
    }
  }, []);
   const createAcademicYear = async () => {
  if (!academicYearForm.name.trim()) {
    return toast.error(t("academicYear.nameRequired"));
  }

  if (!academicYearForm.start_date || !academicYearForm.end_date) {
    return toast.error(t("academicYear.datesRequired"));  
  }

  try {
    setSavingAcademicYear(true);

    await api.post("/school/academic-years", {
      ...academicYearForm,
      name: academicYearForm.name.trim(),
    });

    toast.success(t("academicYear.createdSuccess"));

    setAcademicYearForm({
      name: "",
      start_date: "",
      end_date: "",
      status: "upcoming",
      is_current: false,
    });

    setAcademicYearDialogOpen(false);
    await loadAcademicYears();
  } catch (e) {
    toast.error(formatErr(e, t("academicYear.createFailed")));
  } finally {
    setSavingAcademicYear(false);
  }
};
  const setCurrentAcademicYear = async (academicYearId) => {
    try {
      await api.patch(`/school/academic-years/${academicYearId}/set-current`);
      toast.success(t("academicYear.updatedSuccess"));  
      await loadAcademicYears();
    } catch (e) {
      toast.error(formatErr(e, t("academicYear.updateFailed")));
    }
};

  const returnToParentDashboard = async () => {
    if (!user?.access_context || returningHome) return;

    try {
      setReturningHome(true);
      const data = await returnHome();
      navigate(data?.user?.role === "super_admin" ? "/admin" : "/organization");
    } catch (e) {
      toast.error(formatErr(e, t("organization.returnHomeFailed", { defaultValue: "Unable to return to dashboard" })));
    } finally {
      setReturningHome(false);
    }
  };

  useEffect(() => {
  loadAcademicYears();
}, [loadAcademicYears]);

useEffect(() => {
  load();
}, [load]);

  useEffect(() => {
    api.get("/learning/context").then(({ data }) => setOrganizationContext(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedAcademicYearId) return;
    // Large student lists are no longer loaded on dashboard/Students startup.
    // Legacy tabs that still need a full selector load them only when opened.
    if (!["fees", "leaves", "report-cards"].includes(tab)) return;
    api.get("/school/students", { params: { academic_year_id: selectedAcademicYearId } })
      .then(({ data }) => setStudents(Array.isArray(data) ? data : []))
      .catch((e) => toast.error(formatErr(e)));
  }, [selectedAcademicYearId, tab]);
  return (
    <DashboardShell brand={organizationType === "phonetics" ? "Centre Admin" : isSchool ? "School Admin" : "Organisation Admin"} nav={dynamicNav} active={tab} onNavigate={setTab}>
      {user?.access_context && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 rounded-2xl border border-status-blue/20 bg-status-blue/5 px-4 py-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-status-blue">
              {user.access_context === "super_admin"
                ? "EDUJAC"
                : t("organization.organizationDashboard", { defaultValue: "Organization Dashboard" })}
            </p>
            <p className="text-sm font-semibold text-slate-700">
              {user.active_branch_name || t("organization.branch", { defaultValue: "Branch" })}
              {user.active_branch_code ? ` · ${user.active_branch_code}` : ""}
            </p>
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="return-parent-dashboard"
            onClick={returnToParentDashboard}
            disabled={returningHome}
            className="rounded-full border-status-blue/30 text-status-blue hover:text-status-blue"
          >
            {returningHome
              ? t("common.loading", { defaultValue: "Loading..." })
              : `${t("common.back", { defaultValue: "Back" })} · ${
                  user.access_context === "super_admin"
                    ? "EDUJAC"
                    : t("organization.organizationDashboard", { defaultValue: "Organization Dashboard" })
                }`}
          </Button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 bg-white border border-slate-200 rounded-2xl px-4 py-3">
        <div>
          <p className="text-xs font-semibold text-slate-500">
            {t("academicYear.label")}
          </p>
          <p className="text-xs text-slate-400">
            {t("academicYear.contextNote")}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
  {academicYears.length > 0 && (
    <Select
      value={selectedAcademicYearId}
      onValueChange={setSelectedAcademicYearId}
    >
      <SelectTrigger
        data-testid="academic-year-select"
        className="w-44 bg-white"
      >
        <SelectValue placeholder={t("academicYear.select")} />
      </SelectTrigger>

      <SelectContent>
        {academicYears.map((year) => (
          <SelectItem key={year.id} value={year.id}>
            {year.name}
            {year.is_current ? ` · ${t("academicYear.current")}` : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )}

  <Dialog
    open={academicYearDialogOpen}
    onOpenChange={setAcademicYearDialogOpen}
  >
    <DialogTrigger asChild>
      <Button size="sm" className="gap-2">
        <Plus size={16} />
        {academicYears.length > 0 ? t("academicYear.manage") : t("academicYear.create")}
      </Button>
    </DialogTrigger>

    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{academicYears.length > 0 ? t("academicYear.manage") : t("academicYear.create")}</DialogTitle>
      </DialogHeader> 
        {academicYears.length > 0 && (
          <div className="space-y-2">
            <Label>{t("academicYear.existing")}</Label>
            <div className="space-y-2">
              {academicYears.map((year) => (
                <div
                  key={year.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2"
                >
                  <div>
                    <p className="text-sm font-semibold text-slate-700">
                      {year.name}
                    </p>
                    <p className="text-xs text-slate-400 capitalize">
                      {t(`academicYear.${year.status}`, year.status)}
                      {year.is_current ? ` · ${t("academicYear.current")}` : ""}
                    </p>
                  </div>}

                  {!year.is_current && year.status !== "closed" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentAcademicYear(year.id)}
                    >
                      {t("academicYear.setCurrent")}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
)}
    
      <div className="space-y-4">
        <div>
          <Label>{t("academicYear.name")}</Label>
          <Input
            placeholder="2026-27"
            value={academicYearForm.name}
            onChange={(e) =>
              setAcademicYearForm((prev) => ({
                ...prev,
                name: e.target.value,
              }))
            }
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>{t("academicYear.startDate")}</Label>
            <Input
              type="date"
              value={academicYearForm.start_date}
              onChange={(e) =>
                setAcademicYearForm((prev) => ({
                  ...prev,
                  start_date: e.target.value,
                }))
              }
            />
          </div>

          <div>
            <Label>{t("academicYear.endDate")}</Label>
            <Input
              type="date"
              value={academicYearForm.end_date}
              onChange={(e) =>
                setAcademicYearForm((prev) => ({
                  ...prev,
                  end_date: e.target.value,
                }))
              }
            />
          </div>
        </div>

        <div>
          <Label>{t("academicYear.status")}</Label>
          <Select
            value={academicYearForm.status}
            onValueChange={(value) =>
              setAcademicYearForm((prev) => ({
                ...prev,
                status: value,
                is_current: value === "active",
              }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="upcoming">{t("academicYear.upcoming")}</SelectItem>
              <SelectItem value="active">
                {t("academicYear.current")} / {t("academicYear.active")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button
          className="w-full"
          onClick={createAcademicYear}
          disabled={savingAcademicYear}
        >
          {savingAcademicYear ? `${t("academicYear.create")}...` : t("academicYear.create")}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</div>
      </div>
      {tab === "overview" && stats && <Overview stats={stats} classes={classes} />}
      {tab === "messages" && <AdminMessages />}
      {tab === "teachers" && <TeachersTab teachers={teachers} reload={load} />}
      {tab === "classes" && <ClassesTab classes={classes} teachers={teachers} reload={load} />}
      {tab === "students" && (
        <StudentsTab students={students} classes={classes} classFilter={classFilter} setClassFilter={setClassFilter} reload={load} academicYears={academicYears} selectedAcademicYearId={selectedAcademicYearId} />
      )}
      {tab === "learning" && <LearningManagement academicYearId={selectedAcademicYearId} />}
      {tab === "attendance" && stats && <AttendanceSection stats={stats} classes={classes} />}
      {tab === "broadcasts" && <BroadcastsTab broadcasts={broadcasts} stats={stats} reload={load} />}
      {tab === "school-moments" && <SchoolMoments classes={classes} academicYearId={selectedAcademicYearId} />}
      {tab === "fees" && <FeesTab ledger={ledger} students={students} classes={classes} reload={load} academicYearId={selectedAcademicYearId} />}
      {tab === "leaves" && <LeavesTab students={students} />}
      {tab === "staff-attendance" && <StaffMaster academicYearId={selectedAcademicYearId} ManualAttendance={StaffAttendanceTab} />}
      {tab === "visitors" && <VisitorsTab />}
      {tab === "exams" && <ExamsTab classes={classes} />}
      {tab === "report-cards" && <ReportCardsTab students={students} classes={classes} />}
      {tab === "timetable" && <TimetableTab />}
      {tab === "reports" && <ReportsTab classes={classes} />}
      {tab === "school-profile" && <SchoolProfileTab />}
    </DashboardShell>
  );
}

function Overview({ stats, classes }) {
  const { t } = useTranslation();
  const [rep, setRep] = useState({ class_id: "", from: daysAgoLocal(30), to: todayLocal() });

  const downloadReport = async (fmt) => {
    try {
      const res = await api.get("/school/attendance/export", {
        params: { from: rep.from, to: rep.to, class_id: rep.class_id || undefined, format: fmt },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance_${rep.from}_to_${rep.to}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatErr(e, "Export failed"));
    }
  };

  const printReport = () => {
    window.open(`/print/attendance?from=${rep.from}&to=${rep.to}${rep.class_id ? `&class_id=${rep.class_id}` : ""}`, "_blank");
  };

  const cards = [
  { label: t("dashboard.students"), value: stats.students, tint: "bg-brand-mint/15 text-teal-600", icon: GraduationCap },
  { label: t("dashboard.teachers"), value: stats.teachers, tint: "bg-status-blue/10 text-status-blue", icon: Users },
  { label: t("dashboard.presentToday"), value: stats.present_today, tint: "bg-status-green/10 text-status-green", icon: ChartBar },
  { label: t("dashboard.absentToday"), value: stats.absent_today, tint: "bg-status-red/10 text-status-red", icon: ChartBar },
];
  return (
    <div data-testid="school-overview">
      <h1 className="font-heading text-2xl font-extrabold mb-6">{t("dashboard.todayAtGlance")}</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-slate-200 rounded-2xl p-5 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgb(0,0,0,0.05)] transition-all duration-300">
            <div className={`w-10 h-10 rounded-xl grid place-items-center ${c.tint}`}>
              <c.icon size={20} weight="duotone" />
            </div>
            <p className="font-heading text-3xl font-extrabold mt-3">{c.value}</p>
            <p className="text-sm text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="font-heading font-bold">{t("dashboard.feeHealth")}</h2>
          <div className="mt-4 space-y-3">
            <div className="flex justify-between text-sm"><span className="text-slate-500">{t("dashboard.invoiced")}</span><span className="font-bold">{inr(stats.fees_invoiced)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">{t("dashboard.collected")}</span><span className="font-bold text-status-green">{inr(stats.fees_collected)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-slate-500">{t("dashboard.outstanding")}</span><span className="font-bold text-status-red">{inr(stats.fees_due)}</span></div>
            <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-status-green rounded-full transition-all duration-500"
                style={{ width: `${stats.fees_invoiced ? Math.round((stats.fees_collected / stats.fees_invoiced) * 100) : 0}%` }}
              />
            </div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          <h2 className="font-heading font-bold">{t("dashboard.attendanceToday")}</h2>
          <div className="mt-4 flex gap-3">
            <div className="flex-1 bg-status-green/10 rounded-xl p-4 text-center">
              <p className="font-heading text-2xl font-extrabold text-status-green">{stats.present_today}</p>
              <p className="text-xs text-slate-500 mt-1">{t("dashboard.present")}</p>
            </div>
            <div className="flex-1 bg-status-red/10 rounded-xl p-4 text-center">
              <p className="font-heading text-2xl font-extrabold text-status-red">{stats.absent_today}</p>
              <p className="text-xs text-slate-500 mt-1">{t("dashboard.absent")}</p>
            </div>
            <div className="flex-1 bg-slate-100 rounded-xl p-4 text-center">
              <p className="font-heading text-2xl font-extrabold text-slate-500">{stats.unmarked_today}</p>
              <p className="text-xs text-slate-500 mt-1">{t("dashboard.unmarked")}</p>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl p-6 mt-4" data-testid="attendance-report-card">
        <h2 className="font-heading font-bold">{t("dashboard.attendanceReport")}</h2>
        <p className="text-xs text-slate-500 mb-4 mt-1">{t("dashboard.attendanceReportDescription")}</p>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={rep.class_id || "all"} onValueChange={(v) => setRep({ ...rep, class_id: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="report-class-select" className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("dashboard.allClasses")}</SelectItem>
              {classes.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input type="date" data-testid="report-from" value={rep.from} onChange={(e) => setRep({ ...rep, from: e.target.value })} className="w-40" />
          <span className="text-slate-400 text-sm">{t("common.to")}</span>
          <Input type="date" data-testid="report-to" value={rep.to} onChange={(e) => setRep({ ...rep, to: e.target.value })} className="w-40" />
          <Button variant="outline" data-testid="export-csv-btn" onClick={() => downloadReport("csv")} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
            <DownloadSimple size={16} className="mr-1.5" /> {t("dashboard.csv")}
          </Button>
          <Button variant="outline" data-testid="export-excel-btn" onClick={() => downloadReport("xlsx")} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
            <DownloadSimple size={16} className="mr-1.5" /> {t("dashboard.excel")}
          </Button>
          <Button variant="outline" data-testid="print-report-btn" onClick={printReport} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
            <Printer size={16} className="mr-1.5" /> {t("dashboard.print")}
          </Button>
        </div>
      </div>
    </div>
  );
}

function AttendanceSection({ stats, classes }) {
  const { t } = useTranslation();
  const [sub, setSub] = useState("overview");
  const [rep, setRep] = useState({ class_id: "", from: daysAgoLocal(30), to: todayLocal() });

  const downloadReport = async (fmt) => {
    try {
      const res = await api.get("/school/attendance/export", {
        params: { from: rep.from, to: rep.to, class_id: rep.class_id || undefined, format: fmt },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance_${rep.from}_to_${rep.to}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatErr(e, "Export failed"));
    }
  };

  const printReport = () => {
    window.open(`/print/attendance?from=${rep.from}&to=${rep.to}${rep.class_id ? `&class_id=${rep.class_id}` : ""}`, "_blank");
  };

  return (
    <div data-testid="attendance-section">
      <h1 className="font-heading text-2xl font-extrabold mb-4">{t("navigation.attendance")}</h1>
      <div className="flex gap-2 mb-5 overflow-x-auto scrollbar-none" data-testid="attendance-subnav">
        {[["overview", t("attendance.overview")], ["mark", t("attendance.markAttendance")], ["daily", t("attendance.dailyReport")], ["monthly", t("attendance.monthlyReport")], ["temp", t("attendance.temporaryAccess")]].map(([id, label]) => (
          <button
            key={id}
            data-testid={`attendance-sub-${id}`}
            onClick={() => setSub(id)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-bold whitespace-nowrap shrink-0 transition-colors duration-200 ${sub === id ? "bg-[#00194E] text-white" : "bg-white text-slate-500 border border-slate-200 hover:border-slate-300"}`}
          >
            {label}
          </button>
        ))}
      </div>

      {sub === "overview" && (
        <div className="space-y-4" data-testid="attendance-sub-overview">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h2 className="font-heading font-bold">{t("dashboard.attendanceToday")}</h2>
            <div className="mt-4 flex gap-3">
              <div className="flex-1 bg-status-green/10 rounded-xl p-4 text-center">
                <p className="font-heading text-2xl font-extrabold text-status-green">{stats.present_today}</p>
                <p className="text-xs text-slate-500 mt-1">{t("dashboard.present")}</p>
              </div>
              <div className="flex-1 bg-status-red/10 rounded-xl p-4 text-center">
                <p className="font-heading text-2xl font-extrabold text-status-red">{stats.absent_today}</p>
                <p className="text-xs text-slate-500 mt-1">{t("dashboard.absent")}</p>
              </div>
              <div className="flex-1 bg-slate-100 rounded-xl p-4 text-center">
                <p className="font-heading text-2xl font-extrabold text-slate-500">{stats.unmarked_today}</p>
                <p className="text-xs text-slate-500 mt-1">{t("dashboard.unmarked")}</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="attendance-export-card">
            <h2 className="font-heading font-bold">{t("attendance.detailedExport")}</h2>
            <p className="text-xs text-slate-500 mb-4 mt-1">{t("attendance.exportDescription")}</p>
            <div className="flex flex-wrap items-center gap-3">
              <Select value={rep.class_id || "all"} onValueChange={(v) => setRep({ ...rep, class_id: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="att-rep-class-select" className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("dashboard.allClasses")}</SelectItem>
                  {classes.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input type="date" data-testid="att-report-from" value={rep.from} onChange={(e) => setRep({ ...rep, from: e.target.value })} className="w-40" />
              <span className="text-slate-400 text-sm">{t("common.to")}</span>
              <Input type="date" data-testid="att-report-to" value={rep.to} onChange={(e) => setRep({ ...rep, to: e.target.value })} className="w-40" />
              <Button variant="outline" data-testid="att-export-csv-btn" onClick={() => downloadReport("csv")} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
                <DownloadSimple size={16} className="mr-1.5" /> {t("dashboard.csv")}
              </Button>
              <Button variant="outline" data-testid="att-export-excel-btn" onClick={() => downloadReport("xlsx")} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
                <DownloadSimple size={16} className="mr-1.5" /> {t("dashboard.excel")}
              </Button>
              <Button variant="outline" data-testid="att-print-report-btn" onClick={printReport} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
                <Printer size={16} className="mr-1.5" /> {t("dashboard.print")}
              </Button>
            </div>
          </div>
        </div>
      )}
      {sub === "mark" && <MarkAttendance optIn={stats.notifications_opt_in} showExport={false} />}
      {sub === "daily" && <DailyReport apiBase="/school/attendance" classes={classes} showSummary />}
      {sub === "monthly" && <MonthlyReport apiBase="/school/attendance" classes={classes} />}
      {sub === "temp" && <TempAccessPanel classes={classes} />}
    </div>
  );
}

function TeachersTab({ teachers, reload }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [editingT, setEditingT] = useState(null);
  const [editT, setEditT] = useState(null);
  const [tSearch, setTSearch] = useState("");

  const downloadTeachers = async (fmt) => {
    try {
      const res = await api.get("/school/teachers/export", { params: { format: fmt }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `teachers.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatErr(e, "Export failed"));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/school/teachers", form);
      toast.success(`Teacher added. Login: ${form.email}`);
      setOpen(false);
      setForm({ name: "", email: "", password: "", phone: "" });
      reload();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/school/teachers/${id}`);
      toast.success("Teacher removed");
      reload();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const openEditTeacher = (t) => {
    setEditingT(t);
    setEditT({ name: t.name, email: t.email, phone: t.phone || "", password: "" });
  };

  const saveEditTeacher = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/school/teachers/${editingT.id}`, { name: editT.name, email: editT.email, phone: editT.phone, password: editT.password || null });
      toast.success("Teacher updated");
      setEditingT(null);
      reload();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  return (
    <div data-testid="teachers-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-heading text-2xl font-extrabold">{t("navigation.teachers")}</h1>
        <div className="flex flex-wrap items-center gap-2">
          <Input data-testid="teacher-search-input" placeholder="Search teacher…" value={tSearch} onChange={(e) => setTSearch(e.target.value)} className="w-44 bg-white" />
          <Button variant="outline" data-testid="teachers-csv-btn" onClick={() => downloadTeachers("csv")} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
            <DownloadSimple size={15} className="mr-1" /> CSV
          </Button>
          <Button variant="outline" data-testid="teachers-excel-btn" onClick={() => downloadTeachers("xlsx")} className="rounded-full font-bold border-slate-300 transition-transform duration-300 active:scale-95">
            <DownloadSimple size={15} className="mr-1" /> Excel
          </Button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-teacher-btn" className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold transition-transform duration-300 active:scale-95">
              <Plus size={16} className="mr-1.5" /> Add teacher
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle className="font-heading">Add a teacher</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div><Label>{t("common.name")}</Label><Input data-testid="teacher-name-input" required className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>{t("common.email")}</Label><Input data-testid="teacher-email-input" type="email" required className="mt-1.5" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label>{t("common.password")}</Label><Input data-testid="teacher-password-input" required className="mt-1.5" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div><Label>{t("common.phone")}</Label><Input className="mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <Button type="submit" data-testid="teacher-submit-btn" disabled={saving} className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold">
                {saving ? "Adding…" : "Add teacher"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>
      <div className="hidden lg:block bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F5F7] text-left text-xs text-slate-500 uppercase tracking-wide">
            <tr><th className="px-5 py-3">{t("common.name")}</th><th className="px-5 py-3">{t("common.email")}</th><th className="px-5 py-3">{t("common.phone")}</th><th className="px-5 py-3">Class teacher of</th><th className="px-5 py-3"></th></tr>
          </thead>
          <tbody>
            {teachers.filter((t) => t.name.toLowerCase().includes(tSearch.toLowerCase())).map((t) => (
              <tr key={t.id} className="border-t border-slate-100" data-testid={`teacher-row-${t.id}`}>
                <td className="px-5 py-3.5 font-semibold">{t.name}</td>
                <td className="px-5 py-3.5 text-slate-600">{t.email}</td>
                <td className="px-5 py-3.5 text-slate-600">{t.phone || "—"}</td>
                <td className="px-5 py-3.5">{(t.classes || []).join(", ") || "—"}</td>
                <td className="px-5 py-3.5 text-right whitespace-nowrap">
                  <button data-testid={`edit-teacher-${t.id}`} onClick={() => openEditTeacher(t)} className="p-2 rounded-lg text-slate-400 hover:text-status-blue hover:bg-status-blue/10 transition-colors duration-200" title="Edit teacher">
                    <PencilSimple size={16} />
                  </button>
                  <button data-testid={`delete-teacher-${t.id}`} onClick={() => remove(t.id)} className="p-2 rounded-lg text-slate-400 hover:text-status-red hover:bg-status-red/10 transition-colors duration-200" title="Delete teacher">
                    <Trash size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="lg:hidden space-y-3" data-testid="teachers-cards">
        {teachers.filter((t) => t.name.toLowerCase().includes(tSearch.toLowerCase())).map((t) => (
          <div key={t.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`teacher-card-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-heading font-bold truncate">{t.name}</p>
                <p className="text-xs text-slate-500 break-all mt-0.5">{t.email}</p>
                <p className="text-xs text-slate-500 mt-0.5">{t.phone || "—"}</p>
                <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wide mt-2.5">Class teacher of</p>
                <p className="text-xs font-semibold mt-0.5">{(t.classes || []).join(", ") || "—"}</p>
              </div>
              <div className="flex shrink-0">
                <button data-testid={`edit-teacher-m-${t.id}`} onClick={() => openEditTeacher(t)} className="p-2 rounded-lg text-slate-400 hover:text-status-blue hover:bg-status-blue/10 transition-colors duration-200" title="Edit teacher">
                  <PencilSimple size={16} />
                </button>
                <button data-testid={`delete-teacher-m-${t.id}`} onClick={() => remove(t.id)} className="p-2 rounded-lg text-slate-400 hover:text-status-red hover:bg-status-red/10 transition-colors duration-200" title="Delete teacher">
                  <Trash size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editingT} onOpenChange={(o) => !o && setEditingT(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading">Edit teacher</DialogTitle></DialogHeader>
          {editT && (
            <form onSubmit={saveEditTeacher} className="space-y-4" data-testid="edit-teacher-form">
              <div><Label htmlFor="edit-teacher-name">{t("common.name")}</Label><Input id="edit-teacher-name" data-testid="edit-teacher-name" required className="mt-1.5" value={editT.name} onChange={(e) => setEditT({ ...editT, name: e.target.value })} /></div>
              <div><Label htmlFor="edit-teacher-email">{t("common.email")}</Label><Input id="edit-teacher-email" data-testid="edit-teacher-email" type="email" required className="mt-1.5" value={editT.email} onChange={(e) => setEditT({ ...editT, email: e.target.value })} /></div>
              <div><Label htmlFor="edit-teacher-phone">{t("common.phone")}</Label><Input id="edit-teacher-phone" className="mt-1.5" value={editT.phone} onChange={(e) => setEditT({ ...editT, phone: e.target.value })} /></div>
              <div><Label htmlFor="edit-teacher-password">New password (leave blank to keep current)</Label><Input id="edit-teacher-password" data-testid="edit-teacher-password" className="mt-1.5" value={editT.password} onChange={(e) => setEditT({ ...editT, password: e.target.value })} /></div>
              <Button type="submit" data-testid="edit-teacher-save-btn" className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold">{t("common.saveChanges")}</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ClassesTab({ classes, teachers, reload }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", grade: "", teacher_id: "" });
  const [editingC, setEditingC] = useState(null);
  const [editC, setEditC] = useState(null);
  const [deletingC, setDeletingC] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/school/classes", { ...form, teacher_id: form.teacher_id || null });
      toast.success("Class created");
      setOpen(false);
      setForm({ name: "", grade: "", teacher_id: "" });
      reload();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const openEditClass = (c) => {
    setEditingC(c);
    setEditC({ name: c.name, grade: c.grade, teacher_id: (c.teacher_ids || [])[0] || "" });
  };

  const saveEditClass = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/school/classes/${editingC.id}`, editC);
      toast.success("Class updated");
      setEditingC(null);
      reload();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const confirmDeleteClass = async () => {
    try {
      await api.delete(`/school/classes/${deletingC.id}`);
      toast.success("Class deleted");
      setDeletingC(null);
      reload();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  return (
    <div data-testid="classes-tab">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-extrabold">{t("navigation.classes")}</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-class-btn" className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold transition-transform duration-300 active:scale-95">
              <Plus size={16} className="mr-1.5" /> Add class
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle className="font-heading">Add a class</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div><Label>Class name</Label><Input data-testid="class-name-input" required className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nursery B" /></div>
              <div><Label>Grade</Label><Input data-testid="class-grade-input" required className="mt-1.5" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} placeholder="Nursery" /></div>
              <div>
                <Label>Class teacher (optional)</Label>
                <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v })}>
                  <SelectTrigger data-testid="class-teacher-select" className="mt-1.5"><SelectValue placeholder="Assign later" /></SelectTrigger>
                  <SelectContent>
                    {teachers.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" data-testid="class-submit-btn" className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold">Create class</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {classes.map((c) => (
          <div key={c.id} className="bg-white border border-slate-200 rounded-2xl p-5 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgb(0,0,0,0.05)] transition-all duration-300" data-testid={`class-card-${c.id}`}>
            <div className="flex items-start justify-between">
              <p className="font-heading text-lg font-bold">{c.name}</p>
              <div className="flex gap-1">
                <button data-testid={`edit-class-${c.id}`} onClick={() => openEditClass(c)} className="p-1.5 rounded-lg text-slate-400 hover:text-status-blue hover:bg-status-blue/10 transition-colors duration-200" title="Edit class"><PencilSimple size={15} /></button>
                <button data-testid={`delete-class-${c.id}`} onClick={() => setDeletingC(c)} className="p-1.5 rounded-lg text-slate-400 hover:text-status-red hover:bg-status-red/10 transition-colors duration-200" title="Delete class"><Trash size={15} /></button>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">{c.grade}</p>
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="text-slate-600">{(c.teacher_names || []).join(", ") || "No teacher"}</span>
              <span className="font-bold text-brand-coralDark">{c.student_count} students</span>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editingC} onOpenChange={(o) => !o && setEditingC(null)}>
        <DialogContent className="rounded-2xl sm:max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Edit class</DialogTitle></DialogHeader>
          {editC && (
            <form onSubmit={saveEditClass} className="space-y-4" data-testid="edit-class-form">
              <div><Label htmlFor="edit-class-name">Class name</Label><Input id="edit-class-name" data-testid="edit-class-name" required className="mt-1.5" value={editC.name} onChange={(e) => setEditC({ ...editC, name: e.target.value })} /></div>
              <div><Label htmlFor="edit-class-grade">Grade</Label><Input id="edit-class-grade" data-testid="edit-class-grade" required className="mt-1.5" value={editC.grade} onChange={(e) => setEditC({ ...editC, grade: e.target.value })} /></div>
              <div>
                <Label>Class teacher</Label>
                <Select value={editC.teacher_id || "none"} onValueChange={(v) => setEditC({ ...editC, teacher_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="edit-class-teacher" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No teacher</SelectItem>
                    {teachers.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" data-testid="edit-class-save-btn" className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold">{t("common.saveChanges")}</Button>
            </form>
          )}
          {editingC && <ClassSubjects classId={editingC.id} teachers={teachers} />}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletingC} onOpenChange={(o) => !o && setDeletingC(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete {deletingC?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The class will be removed. This only works when no students are assigned to it — move or remove its students first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-class-cancel-btn">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-class-confirm-btn" onClick={confirmDeleteClass} className="bg-status-red hover:bg-status-red/90 text-white">Delete class</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function StudentsTab({ students, classes, classFilter, setClassFilter, reload, academicYears, selectedAcademicYearId }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const emptyStudentForm = { name: "", class_id: "", program_id: "", group_id: "", roll_no: "", dob: "", aadhaar: "", notes: "", fee_amount: "", fee_frequency: "monthly", parent_name: "", parent_email: "", parent_password: "", parent_phone: "", student_email: "", student_phone: "", student_password: "" };
  const [form, setForm] = useState(emptyStudentForm);
  const [learningContext, setLearningContext] = useState(null);
  const [learningPrograms, setLearningPrograms] = useState([]);
  const [learningGroups, setLearningGroups] = useState([]);
  const [editingS, setEditingS] = useState(null);
  const [editS, setEditS] = useState(null);
  const [deletingS, setDeletingS] = useState(null);
  const [sSearch, setSSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageRows, setPageRows] = useState([]);
  const [pageMeta, setPageMeta] = useState({ total: 0, total_pages: 1 });
  const [pageLoading, setPageLoading] = useState(false);
  const [promotionStudents, setPromotionStudents] = useState([]);
  const [reportFor, setReportFor] = useState(null);
  const [student360For, setStudent360For] = useState(null);
  const [student360Loading, setStudent360Loading] = useState(false);
  const [promotingS, setPromotingS] = useState(null);
  const [promotionSaving, setPromotionSaving] = useState(false);
  const [promotionConfirmOpen, setPromotionConfirmOpen] = useState(false);
  const [promotionForm, setPromotionForm] = useState({ target_academic_year_id: "", target_class_id: "", roll_no: "" });
  const [bulkPromotionOpen, setBulkPromotionOpen] = useState(false);
  const [bulkPromotionConfirmOpen, setBulkPromotionConfirmOpen] = useState(false);
  const [bulkPromotionSaving, setBulkPromotionSaving] = useState(false);
  const [bulkPromotionResult, setBulkPromotionResult] = useState(null);
  const [bulkPromotionForm, setBulkPromotionForm] = useState({ source_class_id: "", target_academic_year_id: "", target_class_id: "" });
  const [bulkSelectedStudentIds, setBulkSelectedStudentIds] = useState([]);
  const [promotionAction, setPromotionAction] = useState("");
  const [promotionPickerOpen, setPromotionPickerOpen] = useState(false);
  const [promotionPickerStudentId, setPromotionPickerStudentId] = useState("");
  const [undoPromotionOpen, setUndoPromotionOpen] = useState(false);
  const [undoStudentId, setUndoStudentId] = useState("");
  const [undoConfirmOpen, setUndoConfirmOpen] = useState(false);
  const [undoSaving, setUndoSaving] = useState(false);


  const selectedAcademicYear = academicYears.find((year) => year.id === selectedAcademicYearId);
  const organizationType = learningContext?.organization_type || "school";
  const usesClass = organizationType === "school";
  const groupName = learningContext?.terminology?.class || "Batch";

  useEffect(() => {
    Promise.all([
      api.get("/learning/context"),
      api.get("/learning/programs"),
      api.get("/learning/groups", { params: selectedAcademicYearId ? { academic_year_id: selectedAcademicYearId } : undefined }),
    ]).then(([ctx, programs, groups]) => {
      setLearningContext(ctx.data);
      setLearningPrograms(programs.data || []);
      setLearningGroups(groups.data || []);
    }).catch(() => {});
  }, [selectedAcademicYearId]);
  const promotionTargetYears = academicYears.filter(
    (year) => selectedAcademicYear && year.start_date > selectedAcademicYear.start_date
  );
  const promotableStudents = promotionStudents.filter((student) => !student.promoted_to_enrollment_id);
  const undoCandidates = promotionStudents.filter((student) => student.promoted_to_enrollment_id);
  const safeUndoCandidates = undoCandidates.filter(
    (student) => student.promotion_target_academic_year_status === "upcoming"
      && !student.promotion_target_academic_year_is_current
  );

  const handlePromotionAction = async (value) => {
    setPromotionAction("");
    let available = promotionStudents;
    if (!available.length && selectedAcademicYearId) {
      try {
        const { data } = await api.get("/school/students", { params: { academic_year_id: selectedAcademicYearId } });
        available = Array.isArray(data) ? data : [];
        setPromotionStudents(available);
      } catch (err) {
        toast.error(formatErr(err));
        return;
      }
    }
    if (value === "single") {
      setPromotionPickerStudentId("");
      setPromotionPickerOpen(true);
      return;
    }
    if (value === "bulk") {
      openBulkPromotion(available);
      return;
    }
    if (value === "undo") {
      setUndoStudentId("");
      setUndoPromotionOpen(true);
    }
  };

  const openPromotion = (student) => {
    const firstTargetYear = promotionTargetYears[0];
    setPromotingS(student);
    setPromotionForm({
      target_academic_year_id: firstTargetYear?.id || "",
      target_class_id: "",
      roll_no: student.roll_no || "",
    });
  };

  const requestPromotionConfirmation = (e) => {
    e.preventDefault();
    if (!promotingS || !selectedAcademicYearId) return;
    if (!promotionForm.target_academic_year_id || !promotionForm.target_class_id) {
      toast.error(t("studentPromotion.selectTarget"));
      return;
    }
    setPromotionConfirmOpen(true);
  };

  const confirmPromotion = async () => {
    if (!promotingS || !selectedAcademicYearId) return;

    setPromotionSaving(true);
    try {
      const { data } = await api.post(`/school/students/${promotingS.id}/promote`, {
        source_academic_year_id: selectedAcademicYearId,
        target_academic_year_id: promotionForm.target_academic_year_id,
        target_class_id: promotionForm.target_class_id,
        roll_no: promotionForm.roll_no || null,
      });
      toast.success(
        t("studentPromotion.success", {
          year: data.target_academic_year_name,
          className: data.target_class_name,
        })
      );
      setPromotionConfirmOpen(false);
      setPromotingS(null);
      setPromotionStudents([]);
      await loadStudentPage(page);
      await reload();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setPromotionSaving(false);
    }
  };
  const bulkSourceStudents = promotionStudents.filter(
    (student) => bulkPromotionForm.source_class_id && student.class_id === bulkPromotionForm.source_class_id
  );
  const bulkEligibleStudents = bulkSourceStudents.filter((student) => !student.promoted_to_enrollment_id);

  const openBulkPromotion = (availableStudents = promotionStudents) => {
    const sourceClassId = classFilter !== "all" ? classFilter : "";
    const firstTargetYear = promotionTargetYears[0];
    const sourceStudents = sourceClassId
      ? availableStudents.filter((student) => student.class_id === sourceClassId && !student.promoted_to_enrollment_id)
      : [];
    setBulkPromotionForm({
      source_class_id: sourceClassId,
      target_academic_year_id: firstTargetYear?.id || "",
      target_class_id: "",
    });
    setBulkSelectedStudentIds(sourceStudents.map((student) => student.id));
    setBulkPromotionResult(null);
    setBulkPromotionOpen(true);
  };

  const changeBulkSourceClass = (sourceClassId) => {
    setBulkPromotionForm({ ...bulkPromotionForm, source_class_id: sourceClassId });
    setBulkSelectedStudentIds(
      promotionStudents
        .filter((student) => student.class_id === sourceClassId && !student.promoted_to_enrollment_id)
        .map((student) => student.id)
    );
  };

  const toggleBulkStudent = (studentId, checked) => {
    setBulkSelectedStudentIds((current) =>
      checked ? [...new Set([...current, studentId])] : current.filter((id) => id !== studentId)
    );
  };

  const requestBulkPromotionConfirmation = (e) => {
    e.preventDefault();
    if (!selectedAcademicYearId || !bulkPromotionForm.source_class_id || !bulkPromotionForm.target_academic_year_id || !bulkPromotionForm.target_class_id) {
      toast.error("Select source class, target academic year and target class");
      return;
    }
    if (!bulkSelectedStudentIds.length) {
      toast.error("Select at least one student");
      return;
    }
    setBulkPromotionConfirmOpen(true);
  };

  const confirmBulkPromotion = async () => {
    setBulkPromotionSaving(true);
    try {
      const { data } = await api.post("/school/students/promote-bulk", {
        source_academic_year_id: selectedAcademicYearId,
        target_academic_year_id: bulkPromotionForm.target_academic_year_id,
        source_class_id: bulkPromotionForm.source_class_id,
        target_class_id: bulkPromotionForm.target_class_id,
        student_ids: bulkSelectedStudentIds,
      });
      setBulkPromotionResult(data);
      setBulkPromotionConfirmOpen(false);
      toast.success(`${data.promoted_count} student${data.promoted_count === 1 ? "" : "s"} promoted${data.skipped_count ? ` · ${data.skipped_count} skipped` : ""}`);
      await reload();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBulkPromotionSaving(false);
    }
  };

  const requestUndoConfirmation = () => {
    const student = safeUndoCandidates.find((item) => item.id === undoStudentId);
    if (!student) {
      toast.error("Select a promotion that is still safe to undo");
      return;
    }
    setUndoConfirmOpen(true);
  };

  const confirmUndoPromotion = async () => {
    const student = safeUndoCandidates.find((item) => item.id === undoStudentId);
    if (!student) return;

    setUndoSaving(true);
    try {
      await api.post(`/school/students/${student.id}/promotions/${student.promoted_to_enrollment_id}/undo`);
      toast.success(`${student.name}'s promotion was undone`);
      setUndoConfirmOpen(false);
      setUndoPromotionOpen(false);
      setUndoStudentId("");
      setPromotionStudents([]);
      await loadStudentPage(page);
      await reload();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setUndoSaving(false);
    }
  };

  const openStudent360 = async (studentId) => {
    setStudent360Loading(true);
    try {
      const { data } = await api.get(`/school/students/${studentId}/360`);
      setStudent360For(data);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setStudent360Loading(false);
    }
  };
  const loadStudentPage = async (requestedPage = page, searchValue = sSearch) => {
    if (!selectedAcademicYearId) return;
    setPageLoading(true);
    try {
      const { data } = await api.get("/school/students-page", { params: {
        page: requestedPage, page_size: 25, academic_year_id: selectedAcademicYearId,
        class_id: classFilter === "all" ? undefined : classFilter,
        search: searchValue.trim() || undefined,
      }});
      setPageRows(data.items || []);
      setPageMeta({ total: data.total || 0, total_pages: data.total_pages || 1 });
      setPage(data.page || requestedPage);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => loadStudentPage(1, sSearch), 300);
    return () => clearTimeout(timer);
  }, [selectedAcademicYearId, classFilter, sSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = pageRows;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/school/students", { ...form, academic_year_id: selectedAcademicYearId, class_id: usesClass ? form.class_id : null, fee_amount: usesClass ? Number(form.fee_amount) || 0 : 0, parent_password: form.parent_password || null, student_email: form.student_email || null, student_password: form.student_password || null });
      const loginNotes = [data.parent_password ? `Parent: ${data.parent_email} / ${data.parent_password}` : `Parent: existing ${data.parent_email}`, data.student_email ? `Student: ${data.student_email} / ${data.student_password}` : null].filter(Boolean).join(" · ");
      toast.success(`Student added${data.invoices_created ? ` · ${data.invoices_created} fee invoice(s) created` : ""}. ${loginNotes}`, { duration: 10000 });
      setOpen(false);
      setForm(emptyStudentForm);
      reload();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSaving(false);
    }
  };

  const openEditStudent = (s) => {
    setEditingS(s);
    setEditS({ name: s.name, class_id: s.class_id, roll_no: s.roll_no || "", dob: s.dob || "", aadhaar: s.aadhaar || "", notes: s.notes || "", parent_phone: s.parent_phone || "", student_phone: s.student_phone || "", fee_amount: String(s.fee_amount ?? ""), fee_frequency: s.fee_frequency || "monthly" });
  };

  const saveEditStudent = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/school/students/${editingS.id}`, { ...editS, fee_amount: Number(editS.fee_amount) || 0 });
      toast.success("Student updated");
      setEditingS(null);
      await loadStudentPage(page);
      reload();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  const confirmDeleteStudent = async () => {
    try {
      await api.delete(`/school/students/${deletingS.id}`);
      toast.success(`${deletingS.name} removed`);
      setDeletingS(null);
      await loadStudentPage(page);
      reload();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  return (
    <div data-testid="students-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-heading text-2xl font-extrabold">{t("navigation.students")}</h1>
        <div className="flex flex-wrap items-center gap-3">
          <Input data-testid="student-search-input" placeholder="Search student…" value={sSearch} onChange={(e) => setSSearch(e.target.value)} className="w-44 bg-white" />
          <Select value={classFilter} onValueChange={setClassFilter}>
            <SelectTrigger data-testid="student-class-filter" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("dashboard.allClasses")}</SelectItem>
              {classes.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={promotionAction} onValueChange={handlePromotionAction}>
            <SelectTrigger
              data-testid="promotion-actions"
              className="w-44 rounded-full font-bold border-slate-300 bg-white"
            >
              <div className="flex items-center gap-2">
                <ArrowUp size={16} />
                <SelectValue placeholder="Promotion" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="single" disabled={!selectedAcademicYearId || promotionTargetYears.length === 0}>
                Promote student
              </SelectItem>
              <SelectItem value="bulk" disabled={!selectedAcademicYearId || promotionTargetYears.length === 0}>
                Bulk promote
              </SelectItem>
              <SelectItem value="undo" disabled={!selectedAcademicYearId}>
                Undo promotion
              </SelectItem>
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-student-btn" className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold transition-transform duration-300 active:scale-95">
                <Plus size={16} className="mr-1.5" /> Add student
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-heading">Add a student</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div><Label>Student name</Label><Input data-testid="student-name-input" required className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                {usesClass ? <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Class</Label>
                    <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })} required>
                      <SelectTrigger data-testid="student-class-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {classes.map((c) => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Roll no.</Label><Input className="mt-1.5" value={form.roll_no} onChange={(e) => setForm({ ...form, roll_no: e.target.value })} /></div>
                </div> : <div className="space-y-4 rounded-xl bg-slate-50 p-4"><p className="text-xs font-bold text-slate-500 uppercase">Learning enrolment</p><div><Label>Program</Label><Select required value={form.program_id} onValueChange={(v) => setForm({ ...form, program_id: v, group_id: "" })}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Select program"/></SelectTrigger><SelectContent>{learningPrograms.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div><div><Label>{groupName}</Label><Select required value={form.group_id} onValueChange={(v) => setForm({ ...form, group_id: v })}><SelectTrigger className="mt-1.5"><SelectValue placeholder={`Select ${groupName.toLowerCase()}`}/></SelectTrigger><SelectContent>{learningGroups.filter((g) => g.program_id === form.program_id).map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent></Select></div><div><Label>Student/roll reference (optional)</Label><Input className="mt-1.5" value={form.roll_no} onChange={(e) => setForm({ ...form, roll_no: e.target.value })}/></div><p className="text-xs text-slate-500">Any applicable Course Fee structure will create an invoice in the existing Fee Accounts ledger.</p></div>}
                <div className={usesClass ? "grid grid-cols-3 gap-4" : "grid grid-cols-1 gap-4"}>
                  <div><Label>Date of birth</Label><Input data-testid="student-dob-input" type="date" className="mt-1.5" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} /></div>
                  {usesClass && <div><Label>Legacy fee reference (₹)</Label><Input data-testid="student-fee-input" type="number" min="0" className="mt-1.5" value={form.fee_amount} onChange={(e) => setForm({ ...form, fee_amount: e.target.value })} /><p className="text-[10px] text-slate-400 mt-1">Does not create invoices. Use Fee Accounts for actual charges.</p></div>}
                  {usesClass && <div>
                    <Label>Fee plan</Label>
                    <Select value={form.fee_frequency} onValueChange={(v) => setForm({ ...form, fee_frequency: v })}>
                      <SelectTrigger data-testid="student-fee-frequency" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Student Aadhaar (optional)</Label><Input data-testid="student-aadhaar-input" maxLength={12} className="mt-1.5" value={form.aadhaar} onChange={(e) => setForm({ ...form, aadhaar: e.target.value.replace(/\D/g, "") })} placeholder="12-digit number" /></div>
                  <div><Label>Notes (optional)</Label><Input data-testid="student-notes-input" className="mt-1.5" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Allergies, transport, etc." /></div>
                </div>
                <div className="border-t border-slate-100 pt-4">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Student login (optional)</p>
                  <div className="grid sm:grid-cols-3 gap-4 mb-4">
                    <div><Label>Student email</Label><Input data-testid="student-login-email" type="email" className="mt-1.5" value={form.student_email} onChange={(e) => setForm({ ...form, student_email: e.target.value })} placeholder="Required for self-attendance" /></div>
                    <div><Label>Student mobile</Label><Input data-testid="student-login-phone" type="tel" className="mt-1.5" value={form.student_phone} onChange={(e) => setForm({ ...form, student_phone: e.target.value })} placeholder="+91 …" /></div>
                    <div><Label>Password (blank = auto-generated)</Label><Input data-testid="student-login-password" className="mt-1.5" value={form.student_password} onChange={(e) => setForm({ ...form, student_password: e.target.value })} /></div>
                  </div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Parent account</p>
                  <div className="space-y-4">
                    <div><Label>Parent name</Label><Input data-testid="parent-name-input" required className="mt-1.5" value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} /></div>
                    <div><Label>Parent email</Label><Input data-testid="parent-email-input" type="email" required className="mt-1.5" value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} /></div>
                    <div><Label>Parent phone</Label><Input data-testid="parent-phone-input" className="mt-1.5" value={form.parent_phone} onChange={(e) => setForm({ ...form, parent_phone: e.target.value })} placeholder="+91 …" /></div>
                    <div><Label>Parent password (blank = auto-generated)</Label><Input data-testid="parent-password-input" className="mt-1.5" value={form.parent_password} onChange={(e) => setForm({ ...form, parent_password: e.target.value })} /></div>
                  </div>
                </div>
                <Button type="submit" data-testid="student-submit-btn" disabled={saving} className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold">
                  {saving ? "Adding…" : "Add student"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-[#F4F5F7] text-left text-xs text-slate-500 uppercase tracking-wide">
            <tr><th className="px-5 py-3">Student</th><th className="px-5 py-3">Class</th><th className="px-5 py-3">Parent contact</th><th className="px-5 py-3">Aadhaar</th><th className="px-5 py-3">Notes</th><th className="px-5 py-3">Fee plan</th><th className="px-5 py-3 text-right">{t("common.actions")}</th></tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.id} className="border-t border-slate-100" data-testid={`student-row-${s.id}`}>
                <td className="px-5 py-3.5"><span className="font-semibold">{s.name}</span>{s.roll_no && <span className="text-xs text-slate-400 ml-2">#{s.roll_no}</span>}</td>
                <td className="px-5 py-3.5">
                  <span>{s.class_name}</span>
                  {s.promoted_to_enrollment_id && (
                    <span className="block text-xs font-medium text-amber-700 mt-0.5">
                      Promoted to {s.promotion_target_academic_year_name || "next year"}
                      {s.promotion_target_class_name ? ` · ${s.promotion_target_class_name}` : ""}
                    </span>
                  )}
                </td>
                <td className="px-5 py-3.5 text-slate-600">
                  {s.parent_email}
                  {s.parent_phone && <span className="block text-xs text-slate-400">{s.parent_phone}</span>}
                </td>
                <td className="px-5 py-3.5 text-slate-600">{s.aadhaar ? `••••${s.aadhaar.slice(-4)}` : "—"}</td>
                <td className="px-5 py-3.5 text-slate-600 max-w-[140px] truncate" title={s.notes || ""}>{s.notes || "—"}</td>
                <td className="px-5 py-3.5 font-semibold">{inr(s.fee_amount)}<span className="text-xs text-slate-400 font-normal"> /{FREQ_LABEL[s.fee_frequency] || "mo"}</span></td>
                <td className="px-5 py-3.5 text-right whitespace-nowrap">
                  <button data-testid={`report-card-${s.id}`} onClick={() => setReportFor(s.id)} className="p-2 rounded-lg text-slate-400 hover:text-brand-coral hover:bg-brand-coral/10 transition-colors duration-200" title="View report card"><FileText size={16} /></button>
                  <button data-testid={`student-360-${s.id}`} onClick={() => openStudent360(s.id)} className="p-2 rounded-lg text-slate-400 hover:text-brand-coral hover:bg-brand-coral/10 transition-colors duration-200" title="Student 360"><UserCircle size={16} /></button>
                  <button data-testid={`edit-student-${s.id}`} onClick={() => openEditStudent(s)} className="p-2 rounded-lg text-slate-400 hover:text-status-blue hover:bg-status-blue/10 transition-colors duration-200" title="Edit student"><PencilSimple size={16} /></button>
                  <button data-testid={`delete-student-${s.id}`} onClick={() => setDeletingS(s)} className="p-2 rounded-lg text-slate-400 hover:text-status-red hover:bg-status-red/10 transition-colors duration-200" title="Delete student"><Trash size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-3 text-sm text-slate-500">
        <span>{pageLoading ? "Loading students…" : `${pageMeta.total} student${pageMeta.total === 1 ? "" : "s"} · 25 per page`}</span>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" disabled={pageLoading || page <= 1} onClick={() => loadStudentPage(page - 1)}>Previous</Button>
          <span className="text-xs font-semibold">Page {page} of {pageMeta.total_pages}</span>
          <Button type="button" variant="outline" size="sm" disabled={pageLoading || page >= pageMeta.total_pages} onClick={() => loadStudentPage(page + 1)}>Next</Button>
        </div>
      </div>

      <Dialog open={promotionPickerOpen} onOpenChange={setPromotionPickerOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading">Promote student</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
              Academic year: <strong>{selectedAcademicYear?.name || "—"}</strong>
            </div>
            <div>
              <Label>Student</Label>
              <Select value={promotionPickerStudentId} onValueChange={setPromotionPickerStudentId}>
                <SelectTrigger data-testid="promotion-student-select" className="mt-1.5">
                  <SelectValue placeholder="Select student" />
                </SelectTrigger>
                <SelectContent>
                  {promotableStudents.map((student) => (
                    <SelectItem key={student.id} value={student.id}>
                      {student.name} · {student.class_name || "—"}{student.roll_no ? ` · Roll ${student.roll_no}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {promotableStudents.length === 0 && (
              <p className="text-sm text-slate-500">No students in this academic year are available for promotion.</p>
            )}
            <Button
              type="button"
              disabled={!promotionPickerStudentId}
              onClick={() => {
                const student = promotableStudents.find((item) => item.id === promotionPickerStudentId);
                if (!student) return;
                setPromotionPickerOpen(false);
                openPromotion(student);
              }}
              className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold"
            >
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={undoPromotionOpen} onOpenChange={(open) => {
        setUndoPromotionOpen(open);
        if (!open) {
          setUndoStudentId("");
          setUndoConfirmOpen(false);
        }
      }}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading">Undo promotion</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
              Undo is available only while the promoted-to academic year is still upcoming. Once that year becomes current, EDUJAC blocks simple undo to protect attendance, fees and exam history.
            </div>
            <div>
              <Label>Promoted student</Label>
              <Select value={undoStudentId} onValueChange={setUndoStudentId}>
                <SelectTrigger data-testid="undo-promotion-student-select" className="mt-1.5">
                  <SelectValue placeholder="Select promotion" />
                </SelectTrigger>
                <SelectContent>
                  {undoCandidates.map((student) => {
                    const safe = student.promotion_target_academic_year_status === "upcoming"
                      && !student.promotion_target_academic_year_is_current;
                    return (
                      <SelectItem key={student.id} value={student.id} disabled={!safe}>
                        {student.name} · {student.class_name || "—"} → {student.promotion_target_academic_year_name || "—"} · {student.promotion_target_class_name || "—"}{safe ? "" : " · Locked"}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            {undoCandidates.length === 0 && (
              <p className="text-sm text-slate-500">No promotions from the selected academic year are available to undo.</p>
            )}
            {undoCandidates.length > 0 && safeUndoCandidates.length === 0 && (
              <p className="text-sm text-slate-500">Promotion history exists, but none of it is safe for simple undo.</p>
            )}
            <Button
              type="button"
              disabled={!undoStudentId || undoSaving}
              onClick={requestUndoConfirmation}
              className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold"
            >
              Review undo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={undoConfirmOpen} onOpenChange={setUndoConfirmOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Confirm undo promotion</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-600">
                {(() => {
                  const student = safeUndoCandidates.find((item) => item.id === undoStudentId);
                  if (!student) return null;
                  return (
                    <>
                      <p className="font-semibold text-slate-700">{student.name}</p>
                      <div className="rounded-xl bg-slate-50 px-4 py-3 space-y-1">
                        <p>Restore: {selectedAcademicYear?.name || "—"} · {student.class_name || "—"}</p>
                        <p>Remove promoted enrollment: {student.promotion_target_academic_year_name || "—"} · {student.promotion_target_class_name || "—"}</p>
                      </div>
                      <p className="font-medium text-amber-700">The permanent student record is preserved. Only this linked promotion is reversed.</p>
                    </>
                  );
                })()}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={undoSaving}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="undo-promotion-final-confirm"
              onClick={confirmUndoPromotion}
              disabled={undoSaving}
              className="bg-brand-coral hover:bg-brand-coralDark text-white"
            >
              {undoSaving ? "Undoing…" : "Confirm undo"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={bulkPromotionOpen} onOpenChange={(open) => {
        setBulkPromotionOpen(open);
        if (!open) {
          setBulkPromotionConfirmOpen(false);
          setBulkPromotionResult(null);
        }
      }}>
        <DialogContent className="rounded-2xl sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Bulk student promotion</DialogTitle></DialogHeader>

          {bulkPromotionResult ? (
            <div className="space-y-4" data-testid="bulk-promotion-result">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-status-green/10 p-4">
                  <p className="text-xs text-slate-500">Promoted</p>
                  <p className="text-2xl font-bold text-status-green">{bulkPromotionResult.promoted_count}</p>
                </div>
                <div className="rounded-xl bg-amber-50 p-4">
                  <p className="text-xs text-slate-500">Skipped</p>
                  <p className="text-2xl font-bold text-amber-700">{bulkPromotionResult.skipped_count}</p>
                </div>
              </div>
              {bulkPromotionResult.skipped?.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <p className="font-semibold text-sm mb-2">Skipped students</p>
                  <div className="space-y-1 text-xs text-slate-600">
                    {bulkPromotionResult.skipped.map((item) => (
                      <p key={item.student_id}>{item.name || item.student_id} — {item.reason}</p>
                    ))}
                  </div>
                </div>
              )}
              <Button type="button" onClick={() => setBulkPromotionOpen(false)} className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold">Done</Button>
            </div>
          ) : (
            <form onSubmit={requestBulkPromotionConfirmation} className="space-y-4" data-testid="bulk-promotion-form">
              <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
                From academic year: <strong>{selectedAcademicYear?.name || "—"}</strong>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Source class</Label>
                  <Select value={bulkPromotionForm.source_class_id} onValueChange={changeBulkSourceClass}>
                    <SelectTrigger data-testid="bulk-promotion-source-class" className="mt-1.5"><SelectValue placeholder="Select class" /></SelectTrigger>
                    <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Target academic year</Label>
                  <Select value={bulkPromotionForm.target_academic_year_id} onValueChange={(v) => setBulkPromotionForm({ ...bulkPromotionForm, target_academic_year_id: v })}>
                    <SelectTrigger data-testid="bulk-promotion-target-year" className="mt-1.5"><SelectValue placeholder="Select year" /></SelectTrigger>
                    <SelectContent>{promotionTargetYears.map((year) => <SelectItem key={year.id} value={year.id}>{year.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Target class</Label>
                <Select value={bulkPromotionForm.target_class_id} onValueChange={(v) => setBulkPromotionForm({ ...bulkPromotionForm, target_class_id: v })}>
                  <SelectTrigger data-testid="bulk-promotion-target-class" className="mt-1.5"><SelectValue placeholder="Select class" /></SelectTrigger>
                  <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              {bulkPromotionForm.source_class_id && (
                <div className="rounded-xl border border-slate-200 overflow-hidden">
                  <div className="flex items-center justify-between bg-slate-50 px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold">{bulkSourceStudents.length} students in source class</p>
                      {bulkSourceStudents.length !== bulkEligibleStudents.length && (
                        <p className="text-xs text-amber-700 mt-0.5">
                          {bulkSourceStudents.length - bulkEligibleStudents.length} already promoted
                        </p>
                      )}
                    </div>
                    <button type="button" className="text-xs font-bold text-brand-coralDark" onClick={() => setBulkSelectedStudentIds(
                      bulkSelectedStudentIds.length === bulkEligibleStudents.length ? [] : bulkEligibleStudents.map((student) => student.id)
                    )}>
                      {bulkSelectedStudentIds.length === bulkEligibleStudents.length && bulkEligibleStudents.length > 0 ? "Clear eligible" : "Select eligible"}
                    </button>
                  </div>
                  <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                    {bulkSourceStudents.map((student) => {
                      const alreadyPromoted = Boolean(student.promoted_to_enrollment_id);
                      return (
                        <label key={student.id} className={`flex items-center gap-3 px-4 py-3 ${alreadyPromoted ? "bg-slate-50 cursor-not-allowed" : "cursor-pointer hover:bg-slate-50"}`}>
                          <Checkbox
                            checked={!alreadyPromoted && bulkSelectedStudentIds.includes(student.id)}
                            disabled={alreadyPromoted}
                            onCheckedChange={(checked) => toggleBulkStudent(student.id, checked === true)}
                          />
                          <span className="text-sm font-medium flex-1">
                            {student.name}
                            {alreadyPromoted && (
                              <span className="block text-xs font-normal text-amber-700 mt-0.5">
                                Already promoted to {student.promotion_target_academic_year_name || "next year"}
                                {student.promotion_target_class_name ? ` · ${student.promotion_target_class_name}` : ""}
                              </span>
                            )}
                          </span>
                          <span className="text-xs text-slate-400">{student.roll_no ? `Roll ${student.roll_no}` : "No roll no."}</span>
                        </label>
                      );
                    })}
                    {bulkSourceStudents.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No students found in this class for the selected academic year.</p>}
                  </div>
                </div>
              )}

              <p className="text-xs text-amber-700">Promotion creates the next academic-year enrollment and preserves the student's historical enrollment. Students already promoted from this year are shown but cannot be selected again.</p>
              <Button type="submit" disabled={bulkPromotionSaving || !bulkSelectedStudentIds.length} className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold">
                Review {bulkSelectedStudentIds.length} selected student{bulkSelectedStudentIds.length === 1 ? "" : "s"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={bulkPromotionConfirmOpen} onOpenChange={setBulkPromotionConfirmOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Confirm bulk promotion</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-600">
                <p>You are about to promote <strong>{bulkSelectedStudentIds.length} selected student{bulkSelectedStudentIds.length === 1 ? "" : "s"}</strong>.</p>
                <div className="rounded-xl bg-slate-50 px-4 py-3 space-y-1">
                  <p>From: {selectedAcademicYear?.name || "—"} · {classes.find((c) => c.id === bulkPromotionForm.source_class_id)?.name || "—"}</p>
                  <p>To: {academicYears.find((year) => year.id === bulkPromotionForm.target_academic_year_id)?.name || "—"} · {classes.find((c) => c.id === bulkPromotionForm.target_class_id)?.name || "—"}</p>
                </div>
                <p className="font-medium text-amber-700">This changes academic enrollment history. Verify the year, class and selected students before continuing.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkPromotionSaving}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="bulk-promotion-final-confirm" onClick={confirmBulkPromotion} disabled={bulkPromotionSaving} className="bg-brand-coral hover:bg-brand-coralDark text-white">
              {bulkPromotionSaving ? "Promoting…" : "Confirm promotion"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!promotingS} onOpenChange={(o) => !o && setPromotingS(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {t("studentPromotion.title", { name: promotingS?.name || "" })}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={requestPromotionConfirmation} className="space-y-4" data-testid="student-promotion-form">
            <div className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
              {t("studentPromotion.from", { year: selectedAcademicYear?.name || "—", className: promotingS?.class_name || "—" })}
            </div>
            <div>
              <Label>{t("studentPromotion.targetYear")}</Label>
              <Select value={promotionForm.target_academic_year_id} onValueChange={(v) => setPromotionForm({ ...promotionForm, target_academic_year_id: v })}>
                <SelectTrigger data-testid="promotion-year-select" className="mt-1.5"><SelectValue placeholder={t("studentPromotion.selectYear")} /></SelectTrigger>
                <SelectContent>
                  {promotionTargetYears.map((year) => (
                    <SelectItem key={year.id} value={year.id}>{year.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("studentPromotion.targetClass")}</Label>
              <Select value={promotionForm.target_class_id} onValueChange={(v) => setPromotionForm({ ...promotionForm, target_class_id: v })}>
                <SelectTrigger data-testid="promotion-class-select" className="mt-1.5"><SelectValue placeholder={t("studentPromotion.selectClass")} /></SelectTrigger>
                <SelectContent>
                  {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("studentPromotion.rollNo")}</Label>
              <Input data-testid="promotion-roll-no" className="mt-1.5" value={promotionForm.roll_no} onChange={(e) => setPromotionForm({ ...promotionForm, roll_no: e.target.value })} />
            </div>
            <p className="text-xs text-slate-500">{t("studentPromotion.historyNote")}</p>
            <Button type="submit" data-testid="promotion-submit-btn" disabled={promotionSaving} className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold">
              {promotionSaving ? t("studentPromotion.promoting") : t("studentPromotion.confirm")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={promotionConfirmOpen} onOpenChange={setPromotionConfirmOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              {t("studentPromotion.confirm")}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-600">
                <p className="font-semibold text-slate-700">{promotingS?.name}</p>
                <div className="rounded-xl bg-slate-50 px-4 py-3 space-y-1">
                  <p>{t("studentPromotion.from", { year: selectedAcademicYear?.name || "—", className: promotingS?.class_name || "—" })}</p>
                  <p>
                    {t("studentPromotion.targetYear")}: {academicYears.find((year) => year.id === promotionForm.target_academic_year_id)?.name || "—"}
                  </p>
                  <p>
                    {t("studentPromotion.targetClass")}: {classes.find((c) => c.id === promotionForm.target_class_id)?.name || "—"}
                  </p>
                  {promotionForm.roll_no && <p>{t("studentPromotion.rollNo")}: {promotionForm.roll_no}</p>}
                </div>
                <p className="font-medium text-amber-700">{t("studentPromotion.historyNote")}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={promotionSaving}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="promotion-final-confirm-btn"
              onClick={confirmPromotion}
              disabled={promotionSaving}
              className="bg-brand-coral hover:bg-brand-coralDark text-white"
            >
              {promotionSaving ? t("studentPromotion.promoting") : t("studentPromotion.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!editingS} onOpenChange={(o) => !o && setEditingS(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading">Edit student</DialogTitle></DialogHeader>
          {editS && (
            <form onSubmit={saveEditStudent} className="space-y-4" data-testid="edit-student-form">
              <div><Label htmlFor="edit-student-name">Student name</Label><Input id="edit-student-name" data-testid="edit-student-name" required className="mt-1.5" value={editS.name} onChange={(e) => setEditS({ ...editS, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Class</Label>
                  <Select value={editS.class_id} onValueChange={(v) => setEditS({ ...editS, class_id: v })}>
                    <SelectTrigger data-testid="edit-student-class" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {classes.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label htmlFor="edit-student-roll">Roll no.</Label><Input id="edit-student-roll" className="mt-1.5" value={editS.roll_no} onChange={(e) => setEditS({ ...editS, roll_no: e.target.value })} /></div>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div><Label htmlFor="edit-student-dob">Date of birth</Label><Input id="edit-student-dob" type="date" className="mt-1.5" value={editS.dob} onChange={(e) => setEditS({ ...editS, dob: e.target.value })} /></div>
                <div><Label htmlFor="edit-student-fee">Fee amount (₹)</Label><Input id="edit-student-fee" data-testid="edit-student-fee" type="number" min="0" className="mt-1.5" value={editS.fee_amount} onChange={(e) => setEditS({ ...editS, fee_amount: e.target.value })} /></div>
                <div>
                  <Label>Fee plan</Label>
                  <Select value={editS.fee_frequency} onValueChange={(v) => setEditS({ ...editS, fee_frequency: v })}>
                    <SelectTrigger data-testid="edit-student-frequency" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label htmlFor="edit-student-aadhaar">Aadhaar (optional)</Label><Input id="edit-student-aadhaar" data-testid="edit-student-aadhaar" maxLength={12} className="mt-1.5" value={editS.aadhaar} onChange={(e) => setEditS({ ...editS, aadhaar: e.target.value.replace(/\D/g, "") })} /></div>
                <div><Label htmlFor="edit-student-parent-phone">Parent phone</Label><Input id="edit-student-parent-phone" data-testid="edit-student-parent-phone" className="mt-1.5" value={editS.parent_phone} onChange={(e) => setEditS({ ...editS, parent_phone: e.target.value })} /></div>
                <div><Label htmlFor="edit-student-login-phone">Student login mobile</Label><Input id="edit-student-login-phone" data-testid="edit-student-login-phone" type="tel" className="mt-1.5" value={editS.student_phone} onChange={(e) => setEditS({ ...editS, student_phone: e.target.value })} /></div>
                <div><Label htmlFor="edit-student-notes">Notes (optional)</Label><Input id="edit-student-notes" data-testid="edit-student-notes" className="mt-1.5" value={editS.notes} onChange={(e) => setEditS({ ...editS, notes: e.target.value })} /></div>
              </div>
              <Button type="submit" data-testid="edit-student-save-btn" className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold">{t("common.saveChanges")}</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deletingS} onOpenChange={(o) => !o && setDeletingS(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete {deletingS?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the student along with their attendance history and fee records. The parent account stays active.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-student-cancel-btn">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-student-confirm-btn" onClick={confirmDeleteStudent} className="bg-status-red hover:bg-status-red/90 text-white">Delete student</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {reportFor && <ReportCard studentId={reportFor} onClose={() => setReportFor(null)} />}
{student360For && (
  <Dialog open={!!student360For} onOpenChange={(open) => !open && setStudent360For(null)}>
    <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto rounded-2xl">
      <DialogHeader>
        <DialogTitle className="font-heading">
          Student 360 — {student360For.student?.name}
        </DialogTitle>
      </DialogHeader>

     <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
  <div className="p-4 rounded-xl border">
    <div className="mb-2"><strong>{t("feeAccounts.studentUniqueId")}:</strong> <span className="font-mono text-xs">{student360For.student?.student_reference_id || "—"}</span>{student360For.student?.student_reference_id && <Button size="sm" variant="ghost" className="ml-1 h-7" onClick={() => { navigator.clipboard?.writeText(student360For.student.student_reference_id); toast.success(t("feeAccounts.studentIdCopied")); }}>{t("common.copy", "Copy")}</Button>}</div>
    <p><strong>Class:</strong> {student360For.class?.name || "—"}</p>
    <p><strong>Roll No:</strong> {student360For.student?.roll_no || "—"}</p>
    <p><strong>Date of Birth:</strong> {student360For.student?.dob || "—"}</p>
  </div>

  <div className="p-4 rounded-xl border">
    <p><strong>Parent:</strong> {student360For.parent?.name || "—"}</p>
    <p><strong>Email:</strong> {student360For.parent?.email || "—"}</p>
    <p><strong>Phone:</strong> {student360For.parent?.phone || "—"}</p>
  </div>
</div>
<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
  <div className="p-4 rounded-xl border">
    <p className="text-sm text-slate-500">Present - Last 30 Days</p>
    <p className="text-2xl font-bold">
      {student360For.attendance?.present_30d ?? 0}
    </p>
  </div>

  <div className="p-4 rounded-xl border">
    <p className="text-sm text-slate-500">Absent - Last 30 Days</p>
    <p className="text-2xl font-bold">
      {student360For.attendance?.absent_30d ?? 0}
    </p>
  </div>

  <div className="p-4 rounded-xl border">
    <p className="text-sm text-slate-500">Attendance %</p>
    <p className="text-2xl font-bold">
      {student360For.attendance?.percentage_30d ?? "—"}%
    </p>
  </div>
</div>
<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
  <div className="p-4 rounded-xl border">
    <p className="text-sm text-slate-500">Total Invoiced</p>
    <p className="text-2xl font-bold">
      ₹{student360For.fees?.invoiced ?? 0}
    </p>
  </div>

  <div className="p-4 rounded-xl border">
    <p className="text-sm text-slate-500">Total Paid</p>
    <p className="text-2xl font-bold">
      ₹{student360For.fees?.paid ?? 0}
    </p>
  </div>

  <div className="p-4 rounded-xl border">
    <p className="text-sm text-slate-500">Balance Due</p>
    <p className="text-2xl font-bold">
      ₹{student360For.fees?.due ?? 0}
    </p>
  </div>
</div>
<div className="mt-4 p-4 rounded-xl border">
  <p className="text-sm text-slate-500 mb-2">Student Leaves</p>

  {student360For.leaves?.length > 0 ? (
    <div className="space-y-2">
      {student360For.leaves.map((leave, index) => (
        <div key={index} className="p-3 rounded-lg bg-slate-50">
          <p><strong>From:</strong> {leave.from_date || "—"}</p>
          <p><strong>To:</strong> {leave.to_date || "—"}</p>
          <p><strong>Status:</strong> {leave.status || "—"}</p>
          <p><strong>Reason:</strong> {leave.reason || "—"}</p>
        </div>
      ))}
    </div>
  ) : (
    <p className="text-lg font-semibold">No leave records</p>
  )}
</div>
<div className="mt-4 p-4 rounded-xl border">
  <p className="text-sm text-slate-500 mb-2">Exam Results</p>

  {student360For.exam_results?.length > 0 ? (
    <div className="space-y-2">
      {student360For.exam_results.map((result, index) => (
        <div key={index} className="p-3 rounded-lg bg-slate-50">
          <p><strong>Exam:</strong> {result.exam || "—"}</p>
          <p><strong>Subject:</strong> {result.subject || "—"}</p>
          <p>
            <strong>Marks:</strong> {result.marks ?? "—"} / {result.max_marks ?? "—"}
          </p>
          <p><strong>Date:</strong> {result.date || "—"}</p>
        </div>
      ))}
    </div>
  ) : (
    <p className="text-lg font-semibold">No exam results</p>
  )}
</div>

<div className="mt-4 p-4 rounded-xl border">
  <p className="text-sm text-slate-500 mb-2">Subjects & Teachers</p>

  {student360For.subjects_teachers?.length > 0 ? (
    <div className="space-y-2">
      {student360For.subjects_teachers.map((item, index) => (
        <div
          key={index}
          className="p-3 rounded-lg bg-slate-50 flex justify-between gap-4"
        >
          <span className="font-semibold">
            {item.subject_name || "—"}
          </span>
          <span>{item.teacher_name || "—"}</span>
        </div>
      ))}
    </div>
  ) : (
    <p className="text-lg font-semibold">No subject assignments</p>
  )}
</div>

    </DialogContent>
  </Dialog>
)}
    </div>
  );
}

function BroadcastsTab({ broadcasts, stats, reload }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ title: "", message: "", category: "general", send_email: false, send_whatsapp: false, send_sms: false });
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/school/broadcasts", { ...form, attachments });
      const d = data.dispatch;
      toast.success(
        `Broadcast sent to ${data.notified} in-app` +
          (d ? ` · ${d.email_sent} emails${d.email_failed ? ` (${d.email_failed} failed)` : ""} · ${d.whatsapp_sent} WhatsApp${d.whatsapp_failed ? ` (${d.whatsapp_failed} failed)` : ""} · ${d.sms_sent || 0} SMS${d.sms_failed ? ` (${d.sms_failed} failed)` : ""}` : "")
      );
      setForm({ title: "", message: "", category: "general", send_email: false, send_whatsapp: false, send_sms: false });
      setAttachments([]);
      reload();
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSending(false);
    }
  };

  const pickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (attachments.length + files.length > 3) {
      toast.error("Maximum 3 attachments per broadcast");
      return;
    }
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttachments((prev) => [...prev, data]);
      }
    } catch (err) {
      toast.error(formatErr(err, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const catColor = { holiday: "bg-status-blue/10 text-status-blue", event: "bg-brand-mint/15 text-teal-600", fee_reminder: "bg-status-amber/20 text-amber-600", birthday: "bg-brand-coral/10 text-brand-coralDark", general: "bg-slate-100 text-slate-600" };

  return (
    <div data-testid="broadcasts-tab">
      <h1 className="font-heading text-2xl font-extrabold mb-6">Broadcast Hub</h1>
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 mb-6" data-testid="broadcast-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label>Title</Label><Input data-testid="broadcast-title-input" required className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Summer break announcement" /></div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="broadcast-category-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="holiday">Holiday</SelectItem>
                <SelectItem value="event">Event / Meeting</SelectItem>
                <SelectItem value="fee_reminder">Fee Reminder</SelectItem>
                <SelectItem value="birthday">Birthday</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4"><Label>Message</Label><Textarea data-testid="broadcast-message-input" required rows={3} className="mt-1.5" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Write your announcement…" /></div>
        <div className="mt-4">
          <Label>Attachments (up to 3)</Label>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {attachments.map((a) => (
              <span key={a.path} className="inline-flex items-center gap-2 bg-slate-100 rounded-full px-3 py-1.5 text-xs font-semibold" data-testid={`attachment-chip-${a.name}`}>
                <Paperclip size={12} /> {a.name}
                <button type="button" onClick={() => setAttachments(attachments.filter((x) => x.path !== a.path))} className="text-slate-400 hover:text-status-red transition-colors duration-200" aria-label={`Remove ${a.name}`}>
                  <X size={12} />
                </button>
              </span>
            ))}
            {attachments.length < 3 && (
              <label className="inline-flex items-center gap-2 text-xs font-bold text-brand-coralDark bg-brand-coral/10 rounded-full px-3 py-1.5 cursor-pointer hover:bg-brand-coral/20 transition-colors duration-200" data-testid="attachment-upload-btn">
                <Plus size={12} /> {uploading ? "Uploading…" : "Attach file"}
                <input type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.txt,.csv,.doc,.docx,.xls,.xlsx" className="hidden" onChange={pickFiles} />
              </label>
            )}
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2.5 text-sm font-medium cursor-pointer">
            <Checkbox data-testid="broadcast-email-checkbox" checked={form.send_email} onCheckedChange={(c) => setForm({ ...form, send_email: !!c })} />
            Send via Email
          </label>
          <label className="flex items-center gap-2.5 text-sm font-medium cursor-pointer">
            <Checkbox data-testid="broadcast-whatsapp-checkbox" checked={form.send_whatsapp} onCheckedChange={(c) => setForm({ ...form, send_whatsapp: !!c })} />
            Send via WhatsApp
          </label>
          <label className="flex items-center gap-2.5 text-sm font-medium cursor-pointer">
            <Checkbox data-testid="broadcast-sms-checkbox" checked={form.send_sms} onCheckedChange={(c) => setForm({ ...form, send_sms: !!c })} />
            Send via SMS
          </label>
        </div>
        {stats && !stats.notifications_opt_in && (form.send_email || form.send_whatsapp || form.send_sms) && (
          <p className="mt-3 text-xs font-semibold text-amber-700 bg-status-amber/20 rounded-xl px-4 py-2.5" data-testid="opt-in-warning">
            External notifications are disabled for this school. The platform owner must enable the opt-in (Super Admin → Schools → Edit) before Email/WhatsApp dispatch works.
          </p>
        )}
        <Button type="submit" data-testid="broadcast-send-btn" disabled={sending} className="mt-4 rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold px-8 transition-transform duration-300 active:scale-95">
          {sending ? "Sending…" : "Send to all parents"}
        </Button>
      </form>
      <div className="space-y-3">
        {broadcasts.map((b) => (
          <div key={b.id} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`broadcast-card-${b.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${catColor[b.category] || catColor.general}`}>{b.category.replace("_", " ")}</span>
              <p className="font-heading font-bold">{b.title}</p>
              {b.channels?.email && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-status-blue/10 text-status-blue flex items-center gap-1"><EnvelopeSimple size={12} weight="duotone" /> email</span>}
              {b.channels?.whatsapp && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-status-green/10 text-status-green flex items-center gap-1"><WhatsappLogo size={12} weight="duotone" /> whatsapp</span>}
              {b.channels?.sms && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-status-amber/20 text-amber-700 flex items-center gap-1"><ChatText size={12} weight="duotone" /> sms</span>}
              {b.dispatch && <span className="text-xs text-slate-400">({b.dispatch.email_sent} emailed · {b.dispatch.whatsapp_sent} WA)</span>}
            </div>
            <p className="text-sm text-slate-600 mt-2">{b.message}</p>
            {b.attachments?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2.5">
                {b.attachments.map((a) => (
                  <a
                    key={a.path}
                    href="#"
                    onClick={(e) => { e.preventDefault(); openFile(a.path); }}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-status-blue bg-status-blue/10 rounded-full px-3 py-1 hover:bg-status-blue/20 transition-colors duration-200"
                    data-testid={`broadcast-attachment-${a.name}`}
                  >
                    <Paperclip size={12} /> {a.name}
                  </a>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-400 mt-2">by {b.created_by} · {fmtDate(b.created_at)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function FeesTab({ ledger, students, classes, reload, academicYearId }) {
  const { t } = useTranslation();
  const [view, setView] = useState("dashboard");
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [reconciliation, setReconciliation] = useState([]);
  const [statement, setStatement] = useState(null);
  const [statementOpen, setStatementOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [openingOpen, setOpeningOpen] = useState(false);
  const [carryOpen, setCarryOpen] = useState(false);
  const [academicYears, setAcademicYears] = useState([]);
  const [openingForm, setOpeningForm] = useState({ student_id: "", amount: "", note: "" });
  const [carryForm, setCarryForm] = useState({ from_academic_year_id: "", to_academic_year_id: "" });
  const [editInv, setEditInv] = useState(null);
  const [delInv, setDelInv] = useState(null);
  const [invForm, setInvForm] = useState({ category_id: "", title: "", amount: "", due_date: todayLocal(), scope: "class", class_id: "", student_id: "", concession_type: "none", concession_method: "fixed", concession_value: "", discount_reason: "" });
  const [payForm, setPayForm] = useState({ student_id: "", invoice_id: "", amount: "", method: "cash", payment_date: todayLocal(), reference: "", note: "" });
  const [studentSearch, setStudentSearch] = useState("");
  const [ledgerSearch, setLedgerSearch] = useState("");
  const [ledgerClass, setLedgerClass] = useState("all");
  const [ledgerBalance, setLedgerBalance] = useState("all");
  const [txSearch, setTxSearch] = useState("");
  const [txType, setTxType] = useState("all");
  const [txFrom, setTxFrom] = useState("");
  const [txTo, setTxTo] = useState("");
  const [receiptSearch, setReceiptSearch] = useState("");
  const [receiptMode, setReceiptMode] = useState("all");
  const [feeCategories, setFeeCategories] = useState([]);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const PAGE_SIZE = 50;
  const [ledgerPage, setLedgerPage] = useState(1);
  const [txPage, setTxPage] = useState(1);
  const [receiptPage, setReceiptPage] = useState(1);
  const [recPage, setRecPage] = useState(1);

  const refreshFeeViews = useCallback(async () => {
    try {
      const params = academicYearId ? { academic_year_id: academicYearId } : undefined;
      const [s, tx, rec, yrs, cats] = await Promise.all([
        api.get("/school/fees/summary", { params }),
        api.get("/school/fees/transactions", { params }),
        api.get("/school/fees/reconciliation"),
        api.get("/school/academic-years"),
        api.get("/school/fees/categories", { params: { include_inactive: true } }),
      ]);
      setSummary(s.data);
      setTransactions(tx.data || []);
      setReconciliation(rec.data || []);
      setAcademicYears(yrs.data || []);
      setFeeCategories(cats.data || []);
    } catch (e) {
      toast.error(formatErr(e, "Could not load fee accounts"));
    }
  }, [academicYearId]);

  useEffect(() => {
    refreshFeeViews();
  }, [refreshFeeViews]);

  const refreshAll = async () => {
    await reload();
    await refreshFeeViews();
  };

  const studentInvoices = useMemo(() => {
    const row = ledger.find((r) => r.id === payForm.student_id);
    return row ? (row.invoices || []).filter((i) => i.status !== "paid" && i.status !== "cancelled") : [];
  }, [ledger, payForm.student_id]);

  const selectedPaymentStudent = useMemo(() => ledger.find((r) => r.id === payForm.student_id) || null, [ledger, payForm.student_id]);
  const selectedPaymentInvoice = useMemo(
    () => studentInvoices.find((i) => i.id === payForm.invoice_id) || null,
    [studentInvoices, payForm.invoice_id]
  );
  const paymentOutstanding = useMemo(() => {
    if (selectedPaymentInvoice) {
      return Math.max(
        0,
        Number(selectedPaymentInvoice.amount || 0)
          - Number(selectedPaymentInvoice.paid_amount || 0)
          - Number(selectedPaymentInvoice.concession_amount || 0)
      );
    }
    return Math.max(0, Number(selectedPaymentStudent?.balance || 0));
  }, [selectedPaymentInvoice, selectedPaymentStudent]);

  const paymentStudentMatches = useMemo(() => {
    const q = studentSearch.trim().toLowerCase();
    if (selectedPaymentStudent && q === `${selectedPaymentStudent.name} · ${selectedPaymentStudent.student_reference_id || ''}`.toLowerCase()) return [selectedPaymentStudent];
    if (!q) return ledger.slice(0, 12);
    return ledger.filter((st) => [st.name, st.student_reference_id, st.roll_no, st.admission_no, st.class_name].some((v) => String(v || "").toLowerCase().includes(q))).slice(0, 20);
  }, [ledger, studentSearch, selectedPaymentStudent]);

  const filteredLedger = useMemo(() => ledger.filter((row) => {
    const q = ledgerSearch.trim().toLowerCase();
    const textMatch = !q || [row.name, row.student_reference_id, row.admission_no, row.roll_no, row.class_name].some((v) => String(v || '').toLowerCase().includes(q));
    const classMatch = ledgerClass === 'all' || row.class_id === ledgerClass;
    const balance = Number(row.balance || 0);
    const balanceMatch = ledgerBalance === 'all' || (ledgerBalance === 'outstanding' && balance > 0) || (ledgerBalance === 'paid' && Number(row.invoiced || 0) > 0 && balance <= 0) || (ledgerBalance === 'no_invoice' && Number(row.invoiced || 0) === 0) || (ledgerBalance === 'concession' && Number(row.concession || 0) > 0);
    return textMatch && classMatch && balanceMatch;
  }), [ledger, ledgerSearch, ledgerClass, ledgerBalance]);

  const filteredTransactions = useMemo(() => transactions.filter((x) => {
    const q = txSearch.trim().toLowerCase();
    const textMatch = !q || [x.student_name, x.student_reference_id, x.transaction_id, x.receipt_no, x.reference, x.description].some((v) => String(v || '').toLowerCase().includes(q));
    const typeMatch = txType === 'all' || x.transaction_type === txType;
    const d = x.date || x.payment_date || '';
    return textMatch && typeMatch && (!txFrom || d >= txFrom) && (!txTo || d <= txTo);
  }), [transactions, txSearch, txType, txFrom, txTo]);

  const receiptRows = useMemo(() => filteredTransactions.filter((x) => x.transaction_type === 'payment' && x.reconciliation_status === 'reconciled' && (receiptMode === 'all' || x.method === receiptMode) && (!receiptSearch.trim() || [x.student_name, x.student_reference_id, x.receipt_no, x.transaction_id, x.reference].some((v) => String(v || '').toLowerCase().includes(receiptSearch.trim().toLowerCase())))), [filteredTransactions, receiptMode, receiptSearch]);

  const pagedLedger = filteredLedger.slice((ledgerPage - 1) * PAGE_SIZE, ledgerPage * PAGE_SIZE);
  const pagedTransactions = filteredTransactions.slice((txPage - 1) * PAGE_SIZE, txPage * PAGE_SIZE);
  const pagedReceipts = receiptRows.slice((receiptPage - 1) * PAGE_SIZE, receiptPage * PAGE_SIZE);
  const pagedReconciliation = reconciliation.slice((recPage - 1) * PAGE_SIZE, recPage * PAGE_SIZE);

  useEffect(() => setLedgerPage(1), [ledgerSearch, ledgerClass, ledgerBalance, academicYearId]);
  useEffect(() => setTxPage(1), [txSearch, txType, txFrom, txTo, academicYearId]);
  useEffect(() => setReceiptPage(1), [receiptSearch, receiptMode, txSearch, txType, txFrom, txTo, academicYearId]);
  useEffect(() => setRecPage(1), [reconciliation.length]);

  const Pager = ({ page, setPage, total }) => {
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const start = total ? (page - 1) * PAGE_SIZE + 1 : 0;
    const end = Math.min(page * PAGE_SIZE, total);
    return <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t bg-slate-50 text-xs text-slate-600">
      <span>Showing {start}–{end} of {total}</span>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</Button>
        <span className="px-2 font-semibold">Page {page} of {pages}</span>
        <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}>Next</Button>
      </div>
    </div>;
  };

  const addFeeCategory = async (e) => {
    e.preventDefault();
    try {
      await api.post("/school/fees/categories", { name: categoryName });
      toast.success(t("feeAccounts.categoryCreated"));
      setCategoryName("");
      await refreshFeeViews();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const editFeeCategory = async (category) => {
    const name = window.prompt(t("feeAccounts.categoryName"), category.name);
    if (!name) return;
    const categoryType = window.prompt("Category type: major, small or regular", category.category_type || "regular");
    if (!categoryType) return;
    const minimumPayable = window.prompt("Minimum payable amount", String(category.minimum_payable || 0));
    if (minimumPayable === null) return;
    const collectionPriority = window.prompt("Collection priority (1 is first)", String(category.collection_priority || 100));
    if (collectionPriority === null) return;
    const receiptPriority = window.prompt("Receipt display priority (1 is first)", String(category.receipt_priority || 100));
    if (receiptPriority === null) return;
    try {
      await api.patch(`/school/fees/categories/${category.id}`, { name: name.trim(), category_type: categoryType.trim().toLowerCase(),
        minimum_payable: Number(minimumPayable), collection_priority: Number(collectionPriority), receipt_priority: Number(receiptPriority),
        include_in_first_payment: window.confirm("Include this category in the first payment?"), mandatory: true });
      await refreshFeeViews();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const toggleFeeCategory = async (category) => {
    try {
      await api.patch(`/school/fees/categories/${category.id}`, { is_active: !category.is_active });
      await refreshFeeViews();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const submitInvoice = async (e) => {
    e.preventDefault();
    try {
      const gross = Number(invForm.amount);
      const concessionValue = Number(invForm.concession_value || 0);
      const discountAmount = invForm.concession_type === 'none' ? 0 : (invForm.concession_method === 'percent' ? gross * concessionValue / 100 : concessionValue);
      const concessionLabel = invForm.concession_type === 'none' ? null : invForm.concession_type.replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      const payload = { category_id: invForm.category_id, title: invForm.title, amount: gross, due_date: invForm.due_date, discount_amount: Number(discountAmount.toFixed(2)), discount_reason: [concessionLabel, invForm.discount_reason].filter(Boolean).join(' - ') || null };
      if (invForm.scope === "class") payload.class_id = invForm.class_id;
      else payload.student_id = invForm.student_id;
      const { data } = await api.post("/school/fees/invoices", payload);
      toast.success(`Invoice created for ${data.created} student(s)`);
      setInvoiceOpen(false);
      await refreshAll();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    const amount = Number(payForm.amount);
    if (!payForm.student_id) {
      toast.error("Select a student before recording payment.");
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Payment amount must be greater than zero.");
      return;
    }
    if (paymentOutstanding <= 0) {
      toast.error("No outstanding fee remains for this student.");
      return;
    }
    if (amount > paymentOutstanding + 0.005) {
      toast.error(`Payment cannot exceed outstanding balance of ${inr(paymentOutstanding)}.`);
      return;
    }

    try {
      await api.post("/school/fees/payments", {
        student_id: payForm.student_id,
        invoice_id: payForm.invoice_id || null,
        amount,
        method: payForm.method,
        payment_date: payForm.payment_date,
        reference: payForm.reference || null,
        note: payForm.note || null,
      });
      toast.success(payForm.method === "cheque" ? "Payment recorded. Cheque is pending clearance." : "Payment recorded and receipt created. Parent notified.");
      setPaymentOpen(false);
      setPayForm({ student_id: "", invoice_id: "", amount: "", method: "cash", payment_date: todayLocal(), reference: "", note: "" });
      await refreshAll();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const reversePayment = async (payment) => {
    const reason = window.prompt(`Reason for reversing receipt ${payment.receipt_no || payment.transaction_id}:`);
    if (!reason) return;
    try {
      await api.post(`/school/fees/payments/${payment.id}/reverse`, { reason });
      toast.success("Payment reversed. Original transaction retained in the audit trail.");
      await refreshAll();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const saveEditInvoice = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/school/fees/invoices/${editInv.id}`, { title: editInv.title, amount: Number(editInv.amount), due_date: editInv.due_date });
      toast.success("Invoice updated");
      setEditInv(null);
      await refreshAll();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const confirmCancelInvoice = async () => {
    try {
      await api.delete(`/school/fees/invoices/${delInv.id}`);
      toast.success("Invoice cancelled. Financial history was preserved.");
      setDelInv(null);
      await refreshAll();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const confirmSchoolPayment = async (payment) => {
    try {
      await api.post(payment.record_kind === 'payment_transaction'
        ? `/school/fees/payment-transactions/${payment.id}/confirm`
        : `/school/fees/payments/${payment.id}/confirm-school`);
      toast.success("Payment confirmed, reconciled and official receipt enabled");
      await refreshAll();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const rejectSchoolPayment = async (payment) => {
    const reason = window.prompt(`Reason for rejecting ${payment.transaction_id || 'this payment'}:`);
    if (!reason || !reason.trim()) return;
    try {
      await api.post(payment.record_kind === 'payment_transaction'
        ? `/school/fees/payment-transactions/${payment.id}/reject`
        : `/school/fees/payments/${payment.id}/reject-school`, { reason: reason.trim() });
      toast.success("Payment rejected. Original record retained in the audit trail.");
      await refreshAll();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const openStatement = async (studentId) => {
    try {
      const { data } = await api.get(`/school/fees/students/${studentId}/statement`, { params: academicYearId ? { academic_year_id: academicYearId } : undefined });
      setStatement(data);
      setStatementOpen(true);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const downloadReceipt = async (paymentId) => {
    try {
      const res = await api.get(`/school/fees/receipt/${paymentId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fee_receipt_${paymentId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e, "Receipt not available")); }
  };

  const printReceipt = async (paymentId) => {
    const w = window.open("", "_blank");
    if (!w) { toast.error("Allow pop-ups to print the receipt"); return; }
    try {
      const res = await api.get(`/school/fees/receipt/${paymentId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      w.location.href = url;
      w.onload = () => w.print();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { w.close(); toast.error(formatErr(e, "Receipt not available")); }
  };

  const downloadFile = async (path, params, filename) => {
    try {
      const res = await api.get(path, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e, "Download failed")); }
  };

  const addOpeningBalance = async (e) => {
    e.preventDefault();
    if (!academicYearId) return toast.error("Select an academic year first");
    try {
      await api.post("/school/fees/opening-balance", { ...openingForm, amount: Number(openingForm.amount), academic_year_id: academicYearId });
      toast.success("Opening balance added");
      setOpeningOpen(false);
      setOpeningForm({ student_id: "", amount: "", note: "" });
      await refreshAll();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const carryForward = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/school/fees/carry-forward", carryForm);
      toast.success(`${data.created} balance(s) carried forward${data.skipped_existing ? `, ${data.skipped_existing} already existed` : ""}`);
      setCarryOpen(false);
      await refreshAll();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const modeLabel = (m) => ({ cash: "Cash", upi: "UPI", bank: "Bank", bank_transfer: "Bank Transfer", cheque: "Cheque", card: "Card/POS", net_banking: "Net Banking", online: "EDUJAC Online", other: "Other" }[m] || m || "—");
  const recLabel = (r) => ({ reconciled: "Reconciled", rejected: "Rejected", pending_school_confirmation: "Pending school", pending_parent_confirmation: "Pending Review (Old Record)", pending_clearance: "Pending clearance", mismatch: "Mismatch", due: "Due", cancelled: "Cancelled" }[r] || r || "Legacy");
  const recClass = (r) => r === "reconciled" ? "text-status-green bg-status-green/10" : ["mismatch", "rejected"].includes(r) ? "text-status-red bg-status-red/10" : "text-amber-700 bg-status-amber/20";

  return (
    <div data-testid="fees-tab" data-hide-ask-edujac="true" className="min-w-0">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-2xl font-extrabold">Fee Accounts</h1>
          <p className="text-sm text-slate-500 mt-1">Student ledgers, daily collections, two-way reconciliation and audit-ready transactions.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Dialog open={openingOpen} onOpenChange={setOpeningOpen}>
            <DialogTrigger asChild><Button variant="outline" className="rounded-full">Opening balance</Button></DialogTrigger>
            <DialogContent className="rounded-2xl"><DialogHeader><DialogTitle>Add opening balance</DialogTitle></DialogHeader><p className="text-xs text-slate-500">Use only when onboarding an existing school balance into the selected academic year. This becomes part of the existing student fee ledger.</p><form onSubmit={addOpeningBalance} className="space-y-4"><div><Label>Student</Label><Select value={openingForm.student_id} onValueChange={(v) => setOpeningForm({ ...openingForm, student_id: v })}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Select student" /></SelectTrigger><SelectContent>{students.map((st) => <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>)}</SelectContent></Select></div><div><Label>Opening balance (₹)</Label><Input type="number" min="1" required className="mt-1.5" value={openingForm.amount} onChange={(e) => setOpeningForm({ ...openingForm, amount: e.target.value })} /></div><div><Label>Note (optional)</Label><Input className="mt-1.5" value={openingForm.note} onChange={(e) => setOpeningForm({ ...openingForm, note: e.target.value })} placeholder="Previous system closing balance" /></div><Button type="submit" className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white">Add opening balance</Button></form></DialogContent>
          </Dialog>
          <Dialog open={carryOpen} onOpenChange={setCarryOpen}>
            <DialogTrigger asChild><Button variant="outline" className="rounded-full">Carry forward</Button></DialogTrigger>
            <DialogContent className="rounded-2xl"><DialogHeader><DialogTitle>Carry forward outstanding balances</DialogTitle></DialogHeader><p className="text-xs text-slate-500">Creates one traceable previous-year due in the target year for each student with an outstanding source-year balance. Existing carry-forward entries are skipped.</p><form onSubmit={carryForward} className="space-y-4"><div><Label>From academic year</Label><Select value={carryForm.from_academic_year_id} onValueChange={(v) => setCarryForm({ ...carryForm, from_academic_year_id: v })}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Source year" /></SelectTrigger><SelectContent>{academicYears.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent></Select></div><div><Label>To academic year</Label><Select value={carryForm.to_academic_year_id} onValueChange={(v) => setCarryForm({ ...carryForm, to_academic_year_id: v })}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Target year" /></SelectTrigger><SelectContent>{academicYears.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}</SelectItem>)}</SelectContent></Select></div><Button type="submit" className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white">Carry forward outstanding balances</Button></form></DialogContent>
          </Dialog>
          <Dialog open={categoryOpen} onOpenChange={setCategoryOpen}>
            <DialogTrigger asChild><Button variant="outline" className="rounded-full">{t("feeAccounts.feeCategories")}</Button></DialogTrigger>
            <DialogContent className="rounded-2xl"><DialogHeader><DialogTitle>{t("feeAccounts.feeCategoryMaster")}</DialogTitle></DialogHeader>
              <form onSubmit={addFeeCategory} className="flex gap-2"><Input required value={categoryName} onChange={(e) => setCategoryName(e.target.value)} placeholder={t("feeAccounts.categoryName")} /><Button type="submit" className="bg-brand-coral hover:bg-brand-coralDark text-white">{t("common.add")}</Button></form>
              <div className="max-h-72 overflow-y-auto divide-y">{feeCategories.map((c) => <div key={c.id} className="py-3 flex items-center justify-between gap-3"><div><p className="font-semibold">{c.name}</p><p className="text-xs text-slate-500">{c.is_active === false ? t("feeAccounts.inactive") : t("feeAccounts.active")}</p></div><div className="flex gap-1"><Button size="sm" variant="ghost" onClick={() => editFeeCategory(c)}>{t("common.edit", "Edit")}</Button><Button size="sm" variant="outline" onClick={() => toggleFeeCategory(c)}>{c.is_active === false ? t("feeAccounts.activate") : t("feeAccounts.deactivate")}</Button></div></div>)}{feeCategories.length === 0 && <p className="py-6 text-sm text-slate-400 text-center">{t("feeAccounts.noCategories")}</p>}</div>
              <p className="text-xs text-slate-500">{t("feeAccounts.categoryAuditNote")}</p>
            </DialogContent>
          </Dialog>
          <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
            <DialogTrigger asChild><Button variant="outline" className="rounded-full"><Plus size={16} className="mr-1.5" /> New invoice</Button></DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Create fee invoice</DialogTitle></DialogHeader>
              <form onSubmit={submitInvoice} className="space-y-4">
                <div><Label>{t("feeAccounts.feeCategory")}</Label><Select value={invForm.category_id} onValueChange={(v) => setInvForm({ ...invForm, category_id: v })}><SelectTrigger className="mt-1.5"><SelectValue placeholder={t("feeAccounts.selectFeeCategory")} /></SelectTrigger><SelectContent>{feeCategories.filter((c) => c.is_active !== false).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>{feeCategories.filter((c) => c.is_active !== false).length === 0 && <p className="text-xs text-amber-700 mt-1">{t("feeAccounts.createCategoryFirst")}</p>}</div>
                <div><Label>Fee / Invoice title</Label><Input className="mt-1.5" required value={invForm.title} onChange={(e) => setInvForm({ ...invForm, title: e.target.value })} placeholder="Tuition Fee - Term 1" /></div>
                <div className="grid grid-cols-2 gap-4"><div><Label>Amount (₹)</Label><Input type="number" min="1" required className="mt-1.5" value={invForm.amount} onChange={(e) => setInvForm({ ...invForm, amount: e.target.value })} /></div><div><Label>Due date</Label><Input type="date" required className="mt-1.5" value={invForm.due_date} onChange={(e) => setInvForm({ ...invForm, due_date: e.target.value })} /></div></div>
                <div><Label>Assign to</Label><Select value={invForm.scope} onValueChange={(v) => setInvForm({ ...invForm, scope: v })}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="class">Entire class</SelectItem><SelectItem value="student">Single student</SelectItem></SelectContent></Select></div>
                {invForm.scope === "class" ? <Select value={invForm.class_id} onValueChange={(v) => setInvForm({ ...invForm, class_id: v })}><SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger><SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select> : <Select value={invForm.student_id} onValueChange={(v) => setInvForm({ ...invForm, student_id: v })}><SelectTrigger><SelectValue placeholder="Select student" /></SelectTrigger><SelectContent>{students.map((st) => <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>)}</SelectContent></Select>}
                <div><Label>Concession / Discount type</Label><Select value={invForm.concession_type} onValueChange={(v) => setInvForm({ ...invForm, concession_type: v, concession_value: v === 'none' ? '' : invForm.concession_value })}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">No concession</SelectItem><SelectItem value="sibling">Sibling Concession</SelectItem><SelectItem value="scholarship">Scholarship</SelectItem><SelectItem value="staff_child">Staff Child Concession</SelectItem><SelectItem value="management">Management Concession</SelectItem><SelectItem value="early_payment">Early Payment Discount</SelectItem><SelectItem value="merit">Merit / Academic Concession</SelectItem><SelectItem value="sports">Sports Concession</SelectItem><SelectItem value="financial_assistance">Financial Assistance</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></div>
                {invForm.concession_type !== 'none' && <><div className="grid grid-cols-2 gap-4"><div><Label>Method</Label><Select value={invForm.concession_method} onValueChange={(v) => setInvForm({ ...invForm, concession_method: v })}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="fixed">Fixed amount (₹)</SelectItem><SelectItem value="percent">Percentage (%)</SelectItem></SelectContent></Select></div><div><Label>Value</Label><Input type="number" min="0" max={invForm.concession_method === 'percent' ? '100' : undefined} className="mt-1.5" value={invForm.concession_value} onChange={(e) => setInvForm({ ...invForm, concession_value: e.target.value })} placeholder={invForm.concession_method === 'percent' ? '10' : '5000'} /></div></div><div><Label>Reason / remarks</Label><Input className="mt-1.5" value={invForm.discount_reason} onChange={(e) => setInvForm({ ...invForm, discount_reason: e.target.value })} placeholder="Approval / reason" /></div><p className="text-sm font-semibold">Net payable: {inr(Math.max(0, Number(invForm.amount || 0) - (invForm.concession_method === 'percent' ? Number(invForm.amount || 0) * Number(invForm.concession_value || 0) / 100 : Number(invForm.concession_value || 0))))}</p></>}
                <p className="text-xs text-slate-500">The original fee and approved concession remain separately visible in the audit trail and student ledger.</p>
                <Button type="submit" disabled={!invForm.category_id} className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold">{t("feeAccounts.createInvoice")}</Button>
              </form>
            </DialogContent>
          </Dialog>
          <Dialog open={paymentOpen} onOpenChange={setPaymentOpen}>
            <DialogTrigger asChild><Button className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold"><CurrencyInr size={16} className="mr-1.5" /> Record Payment</Button></DialogTrigger>
            <DialogContent className="rounded-2xl">
              <DialogHeader><DialogTitle>Record payment received by school</DialogTitle></DialogHeader>
              <p className="text-xs text-slate-500">Use this for payments received/verified by the school outside the EDUJAC gateway. School-recorded payments do not wait for parent approval; the parent receives a notification. Cheques remain pending until clearance.</p>
              <form onSubmit={submitPayment} className="space-y-4">
                <div><Label>Find student</Label><Input className="mt-1.5" value={studentSearch} onChange={(e) => { setStudentSearch(e.target.value); if (payForm.student_id) setPayForm({ ...payForm, student_id: "", invoice_id: "" }); }} placeholder="Type name, Student Ref ID, admission no., roll no. or class" /><div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded-xl divide-y">{paymentStudentMatches.map((st) => <button type="button" key={st.id} onClick={() => { setPayForm({ ...payForm, student_id: st.id, invoice_id: "" }); setStudentSearch(`${st.name} · ${st.student_reference_id || ''}`); }} className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${payForm.student_id === st.id ? 'bg-brand-coral/10' : ''}`}><span className="font-semibold">{st.name}</span><span className="text-xs text-slate-500 block">{st.student_reference_id} · {st.class_name || 'No class'}{st.roll_no ? ` · Roll ${st.roll_no}` : ''}</span></button>)}{paymentStudentMatches.length === 0 && <p className="p-3 text-sm text-slate-400">No matching student.</p>}</div></div>
                {studentInvoices.length > 0 && <div><Label>Against invoice (optional)</Label><Select value={payForm.invoice_id || "general"} onValueChange={(v) => { const id = v === "general" ? "" : v; const inv = studentInvoices.find((i) => i.id === id); setPayForm({ ...payForm, invoice_id: id, amount: inv ? String(Math.max(0, inv.amount - (inv.paid_amount || 0) - (inv.concession_amount || 0))) : payForm.amount }); }}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="general">General payment</SelectItem>{studentInvoices.map((i) => <SelectItem key={i.id} value={i.id}>{i.title} — {inr(Math.max(0, i.amount - (i.paid_amount || 0) - (i.concession_amount || 0)))} due</SelectItem>)}</SelectContent></Select></div>}
                <div className="grid grid-cols-2 gap-4"><div><Label>Amount (₹)</Label><Input type="number" min="1" max={paymentOutstanding > 0 ? paymentOutstanding : undefined} required className="mt-1.5" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} />{payForm.student_id && <p className={`mt-1 text-xs ${paymentOutstanding > 0 ? "text-slate-500" : "text-status-red"}`}>{paymentOutstanding > 0 ? `Maximum payable: ${inr(paymentOutstanding)}` : "No outstanding fee remains."}</p>}</div><div><Label>Payment date</Label><Input type="date" required className="mt-1.5" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} /></div></div>
                <div><Label>Mode of payment</Label><Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="bank_transfer">Bank Transfer / NEFT / IMPS / RTGS</SelectItem><SelectItem value="cheque">Cheque</SelectItem><SelectItem value="card">Card / POS</SelectItem><SelectItem value="net_banking">Net Banking</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent></Select></div>
                <div><Label>Reference / UTR / cheque number</Label><Input className="mt-1.5" value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} /></div>
                <div><Label>Remarks</Label><Input className="mt-1.5" value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} /></div>
                <Button type="submit" disabled={!payForm.student_id || paymentOutstanding <= 0} className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold">Save Payment & Generate Receipt</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {[['dashboard','Dashboard'],['students','Student Ledgers'],['transactions','All Transactions'],['receipts','Receipts'],['reconciliation','Reconciliation']].map(([id,label]) => <Button key={id} variant={view === id ? "default" : "outline"} onClick={() => setView(id)} className={`rounded-full ${view === id ? "bg-brand-coral hover:bg-brand-coralDark" : ""}`}>{label}</Button>)}
      </div>

      {view === "dashboard" && <>
        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-5">
          {[
            [t("feeAccounts.todayCollection"), summary?.today, "bg-emerald-50 border-emerald-100", "text-emerald-700"],
            [t("feeAccounts.weekCollection"), summary?.week, "bg-sky-50 border-sky-100", "text-sky-700"],
            [t("feeAccounts.monthCollection"), summary?.month, "bg-violet-50 border-violet-100", "text-violet-700"],
            [t("feeAccounts.academicYearCollection"), summary?.academic_year, "bg-amber-50 border-amber-100", "text-amber-700"],
          ].map(([label,value,cardClass,valueClass]) => <div key={label} className={`${cardClass} border rounded-2xl p-5 shadow-sm`}><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p><p className={`text-2xl font-heading font-extrabold mt-2 ${valueClass}`}>{inr(value || 0)}</p><p className="text-xs text-slate-500 mt-1">{t("feeAccounts.confirmedCollection")}</p></div>)}
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-5">
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm"><p className="text-xs text-slate-400 uppercase font-semibold">{t("feeAccounts.feesCharged")}</p><p className="text-xl font-heading font-extrabold mt-2 text-slate-800">{inr(summary?.fees_charged || 0)}</p></div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm"><p className="text-xs text-slate-400 uppercase font-semibold">{t("feeAccounts.concessions")}</p><p className="text-xl font-heading font-extrabold mt-2 text-indigo-700">{inr(summary?.concessions || 0)}</p></div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm"><p className="text-xs text-slate-400 uppercase font-semibold">{t("feeAccounts.outstanding")}</p><p className="text-xl font-heading font-extrabold mt-2 text-rose-600">{inr(summary?.outstanding || 0)}</p></div>
        </div>

        <div className="grid xl:grid-cols-2 gap-4 mb-5">
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="mb-3"><h3 className="font-heading font-bold">{t("feeAccounts.collectionTrend")}</h3><p className="text-xs text-slate-500">{t("feeAccounts.lastSevenDays")}</p></div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%"><BarChart data={(summary?.daily_collection || []).map((x) => ({ ...x, label: fmtDate(x.date) }))}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="label" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip formatter={(value) => inr(value)} /><Bar dataKey="amount" name={t("feeAccounts.collection")} fill="#2F80ED" radius={[6,6,0,0]} /></BarChart></ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <h3 className="font-heading font-bold">{t("feeAccounts.collectionByPaymentMode")}</h3><p className="text-xs text-slate-500 mb-3">{t("feeAccounts.reconciledPaymentsOnly")}</p>
            {Object.keys(summary?.by_mode || {}).length ? <div className="h-64"><ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={Object.entries(summary?.by_mode || {}).map(([name,value]) => ({ name: modeLabel(name), value }))} dataKey="value" nameKey="name" innerRadius={52} outerRadius={82} paddingAngle={2}>{Object.keys(summary?.by_mode || {}).map((_,i) => <Cell key={i} fill={["#2F80ED","#27AE60","#9B51E0","#F2994A","#EB5757","#56CCF2","#6FCF97"][i % 7]} />)}</Pie><Tooltip formatter={(value) => inr(value)} /><Legend /></PieChart></ResponsiveContainer></div> : <p className="text-sm text-slate-400 py-20 text-center">{t("feeAccounts.noConfirmedCollections")}</p>}
          </div>
        </div>

        <div className="grid xl:grid-cols-2 gap-4 mb-5">
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <h3 className="font-heading font-bold">{t("feeAccounts.feeStatusOverview")}</h3><p className="text-xs text-slate-500 mb-3">{t("feeAccounts.invoiceStatusSummary")}</p>
            <div className="h-64"><ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={[{name:t("feeAccounts.paidFully"),value:summary?.fee_status?.paid || 0},{name:t("feeAccounts.partiallyPaid"),value:summary?.fee_status?.partial || 0},{name:t("feeAccounts.overdue"),value:summary?.fee_status?.overdue || 0},{name:t("feeAccounts.due"),value:summary?.fee_status?.due || 0}].filter((x) => x.value > 0)} dataKey="value" nameKey="name" innerRadius={52} outerRadius={82}>{["#27AE60","#F2C94C","#EB5757","#2F80ED"].map((c,i) => <Cell key={c} fill={c} />)}</Pie><Tooltip /><Legend /></PieChart></ResponsiveContainer></div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
            <div className="flex items-center justify-between gap-2 mb-3"><div><h3 className="font-heading font-bold">{t("feeAccounts.needsReconciliation")}</h3><p className="text-xs text-slate-500">{t("feeAccounts.reviewBeforeReceipt")}</p></div><Button size="sm" variant="outline" onClick={() => setView('reconciliation')} className="rounded-full">{t("feeAccounts.review")}</Button></div>
            <div className="grid grid-cols-3 gap-3 text-center"><div className="bg-amber-50 rounded-xl p-3"><p className="text-xl font-bold">{summary?.pending_school || 0}</p><p className="text-xs text-slate-500">{t("feeAccounts.schoolConfirmation")}</p></div><div className="bg-amber-50 rounded-xl p-3"><p className="text-xl font-bold">{summary?.pending_parent || 0}</p><p className="text-xs text-slate-500">{t("feeAccounts.legacyReview")}</p></div><div className="bg-red-50 rounded-xl p-3"><p className="text-xl font-bold">{summary?.mismatch || 0}</p><p className="text-xs text-slate-500">{t("feeAccounts.mismatch")}</p></div></div>
            <h4 className="font-heading font-bold mt-5 mb-2">{t("feeAccounts.quickActions")}</h4>
            <div className="grid sm:grid-cols-2 gap-2"><Button variant="outline" className="justify-start rounded-xl" onClick={() => setPaymentOpen(true)}><CurrencyInr size={16} className="mr-2" />{t("feeAccounts.recordPayment")}</Button><Button variant="outline" className="justify-start rounded-xl" onClick={() => setInvoiceOpen(true)}><Plus size={16} className="mr-2" />{t("feeAccounts.newInvoice")}</Button><Button variant="outline" className="justify-start rounded-xl" onClick={() => setView('receipts')}><Printer size={16} className="mr-2" />{t("feeAccounts.receipts")}</Button><Button variant="outline" className="justify-start rounded-xl" onClick={() => setView('students')}><Wallet size={16} className="mr-2" />{t("feeAccounts.studentLedgers")}</Button></div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-5 py-4 border-b flex items-center justify-between"><div><h3 className="font-heading font-bold">{t("feeAccounts.recentFeeTransactions")}</h3><p className="text-xs text-slate-500">{t("feeAccounts.latestReconciledPayments")}</p></div><Button size="sm" variant="outline" className="rounded-full" onClick={() => setView('transactions')}>{t("feeAccounts.viewAll")}</Button></div>
          <div className="overflow-x-auto"><table className="w-full text-sm"><thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-4 py-3 text-left">{t("feeAccounts.date")}</th><th className="px-4 py-3 text-left">{t("feeAccounts.student")}</th><th className="px-4 py-3 text-left">{t("feeAccounts.paymentMode")}</th><th className="px-4 py-3 text-left">{t("feeAccounts.receiptNo")}</th><th className="px-4 py-3 text-right">{t("feeAccounts.amount")}</th></tr></thead><tbody>{(summary?.recent_transactions || []).map((x) => <tr key={x.id} className="border-t border-slate-100"><td className="px-4 py-3">{fmtDate(x.date)}</td><td className="px-4 py-3 font-semibold">{x.student_name || '—'}</td><td className="px-4 py-3">{modeLabel(x.method)}</td><td className="px-4 py-3 font-mono text-xs">{x.receipt_no || '—'}</td><td className="px-4 py-3 text-right font-bold text-emerald-700">{inr(x.amount)}</td></tr>)}{!(summary?.recent_transactions || []).length && <tr><td colSpan={5} className="p-8 text-center text-slate-400">{t("feeAccounts.noRecentTransactions")}</td></tr>}</tbody></table></div>
        </div>
      </>}

      {view === "students" && <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden"><div className="p-4 border-b grid md:grid-cols-4 gap-3"><Input value={ledgerSearch} onChange={(e) => setLedgerSearch(e.target.value)} placeholder="Search name / Student Ref ID / admission / roll" /><Select value={ledgerClass} onValueChange={setLedgerClass}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All classes</SelectItem>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select><Select value={ledgerBalance} onValueChange={setLedgerBalance}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All balances</SelectItem><SelectItem value="outstanding">Outstanding</SelectItem><SelectItem value="paid">Fully paid</SelectItem><SelectItem value="no_invoice">No invoice</SelectItem><SelectItem value="concession">Has concession</SelectItem></SelectContent></Select><Button variant="outline" onClick={() => { setLedgerSearch(""); setLedgerClass("all"); setLedgerBalance("all"); }}>Reset</Button></div><div className="px-4 py-2 text-xs text-slate-500">Showing {filteredLedger.length} of {ledger.length} students</div><div className="overflow-x-auto overscroll-x-contain"><table className="w-full min-w-[860px] text-sm"><thead className="bg-[#F4F5F7] text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-4 py-3 text-left">Student Ref ID</th><th className="px-4 py-3 text-left">Student</th><th className="px-4 py-3 text-left">Class</th><th className="px-4 py-3 text-right">Invoiced</th><th className="px-4 py-3 text-right">Concession</th><th className="px-4 py-3 text-right">Paid</th><th className="px-4 py-3 text-right">Balance</th><th className="px-4 py-3 text-right">Actions</th></tr></thead><tbody>{pagedLedger.map((row) => <tr key={row.id} className="border-t border-slate-100"><td className="px-4 py-3 font-mono text-xs">{row.student_reference_id}</td><td className="px-4 py-3 font-semibold">{row.name}</td><td className="px-4 py-3">{row.class_name}</td><td className="px-4 py-3 text-right">{inr(row.invoiced)}</td><td className="px-4 py-3 text-right text-amber-700">{inr(row.concession || 0)}</td><td className="px-4 py-3 text-right text-status-green font-semibold">{inr(row.paid)}</td><td className="px-4 py-3 text-right font-bold">{inr(row.balance)}</td><td className="px-4 py-3"><div className="flex justify-end gap-1"><Button size="sm" variant="outline" onClick={() => openStatement(row.id)}>View ledger</Button>{(row.invoices || []).length > 0 && <Button size="sm" variant="ghost" onClick={() => setEditInv({ ...row.invoices[0] })}><PencilSimple size={15} /></Button>}{(row.invoices || []).length > 0 && <Button size="sm" variant="ghost" onClick={() => setDelInv(row.invoices[0])}><Trash size={15} /></Button>}</div></td></tr>)}</tbody></table></div><Pager page={ledgerPage} setPage={setLedgerPage} total={filteredLedger.length} /></div>}

      {view === "transactions" && <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden"><div className="px-5 py-4 border-b flex flex-wrap items-center justify-between gap-3"><div><h3 className="font-heading font-bold">All School Fee Transactions</h3><p className="text-xs text-slate-500">One chronological register for invoices, concessions and payments across all students.</p></div><div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => downloadFile('/school/fees/transactions/export', { format: 'pdf', academic_year_id: academicYearId || undefined, from_date: txFrom || undefined, to_date: txTo || undefined }, 'fee_transactions.pdf')}><DownloadSimple size={15} className="mr-1" />PDF</Button><Button size="sm" variant="outline" onClick={() => downloadFile('/school/fees/transactions/export', { format: 'xlsx', academic_year_id: academicYearId || undefined, from_date: txFrom || undefined, to_date: txTo || undefined }, 'fee_transactions.xlsx')}><DownloadSimple size={15} className="mr-1" />Excel</Button></div></div><div className="p-4 border-b grid md:grid-cols-5 gap-3"><Input value={txSearch} onChange={(e) => setTxSearch(e.target.value)} placeholder="Student / Ref ID / transaction / receipt" /><Select value={txType} onValueChange={setTxType}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All types</SelectItem><SelectItem value="invoice">Invoices</SelectItem><SelectItem value="payment">Payments</SelectItem><SelectItem value="concession">Concessions</SelectItem></SelectContent></Select><Input type="date" value={txFrom} onChange={(e) => setTxFrom(e.target.value)} /><Input type="date" value={txTo} onChange={(e) => setTxTo(e.target.value)} /><Button variant="outline" onClick={() => { setTxSearch(""); setTxType("all"); setTxFrom(""); setTxTo(""); }}>Reset</Button></div><div className="px-4 py-2 text-xs text-slate-500">Total transactions: {filteredTransactions.length} · Invoices: {filteredTransactions.filter((x) => x.transaction_type === 'invoice').length} · Payments: {filteredTransactions.filter((x) => x.transaction_type === 'payment').length} · Concessions: {filteredTransactions.filter((x) => x.transaction_type === 'concession').length}</div><div className="overflow-x-auto"><table className="w-full text-sm"><thead className="bg-[#F4F5F7] text-xs uppercase tracking-wide text-slate-500"><tr><th className="px-3 py-3 text-left">Date</th><th className="px-3 py-3 text-left">Type / Ref</th><th className="px-3 py-3 text-left">Student</th><th className="px-3 py-3 text-left">Description / Mode</th><th className="px-3 py-3 text-right">Debit</th><th className="px-3 py-3 text-right">Credit</th><th className="px-3 py-3 text-left">Status</th><th></th></tr></thead><tbody>{pagedTransactions.map((x) => <tr key={`${x.transaction_type}-${x.id}`} className="border-t border-slate-100"><td className="px-3 py-3">{fmtDate(x.date || x.payment_date)}</td><td className="px-3 py-3"><p className="text-xs font-bold uppercase">{x.transaction_type}</p><p className="font-mono text-xs text-slate-500">{x.transaction_id}</p></td><td className="px-3 py-3"><p className="font-semibold">{x.student_name}</p><p className="text-xs text-slate-400">{x.student_reference_id}</p></td><td className="px-3 py-3">{x.description || modeLabel(x.method)}{x.reference ? <span className="block text-xs text-slate-400">{x.reference}</span> : null}</td><td className="px-3 py-3 text-right font-bold">{x.debit ? inr(x.debit) : '—'}</td><td className="px-3 py-3 text-right font-bold text-status-green">{x.credit ? inr(x.credit) : '—'}</td><td className="px-3 py-3"><span className={`text-xs font-bold px-2 py-1 rounded-full ${recClass(x.reconciliation_status)}`}>{recLabel(x.reconciliation_status)}</span></td><td className="px-3 py-3">{x.transaction_type === 'payment' && x.reconciliation_status === 'reconciled' ? <div className="flex"><Button size="sm" variant="ghost" onClick={() => downloadReceipt(x.id)} title="Receipt"><Printer size={15} /></Button><Button size="sm" variant="ghost" onClick={() => reversePayment(x)} title="Reverse payment"><X size={15} /></Button></div> : null}</td></tr>)}{filteredTransactions.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-400">No transactions yet.</td></tr>}</tbody></table></div><Pager page={txPage} setPage={setTxPage} total={filteredTransactions.length} /></div>}

      {view === "receipts" && <div data-hide-ask-edujac="true" className="bg-white border border-slate-200 rounded-2xl overflow-hidden"><div className="px-5 py-4 border-b"><h3 className="font-heading font-bold">Receipts Register</h3><p className="text-xs text-slate-500">Confirmed school-wide payment receipts. Invoices and concessions are not duplicated here.</p></div><div className="p-4 border-b grid md:grid-cols-3 gap-3"><Input value={receiptSearch} onChange={(e) => setReceiptSearch(e.target.value)} placeholder="Receipt / Student Ref ID / student / reference" /><Select value={receiptMode} onValueChange={setReceiptMode}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All payment modes</SelectItem><SelectItem value="cash">Cash</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="bank_transfer">Bank Transfer</SelectItem><SelectItem value="cheque">Cheque</SelectItem><SelectItem value="card">Card / POS</SelectItem><SelectItem value="net_banking">Net Banking</SelectItem><SelectItem value="online">EDUJAC Online</SelectItem></SelectContent></Select><Button variant="outline" onClick={() => { setReceiptSearch(""); setReceiptMode("all"); }}>Reset</Button></div><div className="px-4 py-2 text-xs text-slate-500">Confirmed receipts: {receiptRows.length}</div><div className="overflow-x-auto overscroll-x-contain"><table className="w-full min-w-[760px] text-sm"><thead className="bg-[#F4F5F7] text-xs uppercase text-slate-500"><tr><th className="px-3 py-3 text-left">Date</th><th className="px-3 py-3 text-left">Receipt No.</th><th className="px-3 py-3 text-left">Student</th><th className="px-3 py-3 text-left">Mode / Reference</th><th className="px-3 py-3 text-right">Amount</th><th className="px-3 py-3"></th></tr></thead><tbody>{pagedReceipts.map((x) => <tr key={x.id} className="border-t"><td className="px-3 py-3">{fmtDate(x.date)}</td><td className="px-3 py-3 font-mono text-xs">{x.receipt_no}</td><td className="px-3 py-3"><p className="font-semibold">{x.student_name}</p><p className="text-xs text-slate-400">{x.student_reference_id}</p></td><td className="px-3 py-3">{modeLabel(x.method)}<span className="block text-xs text-slate-400">{x.reference || '—'}</span></td><td className="px-3 py-3 text-right font-bold">{inr(x.amount)}</td><td className="px-3 py-3"><div className="flex gap-1"><Button size="sm" variant="outline" onClick={() => downloadReceipt(x.id)}><DownloadSimple size={15} className="mr-1" />PDF</Button><Button size="sm" variant="outline" onClick={() => printReceipt(x.id)}><Printer size={15} className="mr-1" />Print</Button></div></td></tr>)}{receiptRows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-400">No confirmed receipts yet.</td></tr>}</tbody></table></div><Pager page={receiptPage} setPage={setReceiptPage} total={receiptRows.length} /></div>}

      {view === "reconciliation" && <div className="space-y-3"><div className="flex flex-wrap items-center justify-between gap-3"><div><h3 className="font-heading font-bold">Payment Reconciliation & Legacy Review</h3><p className="text-xs text-slate-500">Review parent-reported, legacy, cheque-clearance and mismatch records. Confirming updates the same payment record and enables its official receipt; rejecting keeps the original record with the reason.</p></div><div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => downloadFile('/school/fees/reconciliation/export', { format: 'pdf' }, 'fee_reconciliation.pdf')}><DownloadSimple size={15} className="mr-1" />PDF</Button><Button size="sm" variant="outline" onClick={() => downloadFile('/school/fees/reconciliation/export', { format: 'xlsx' }, 'fee_reconciliation.xlsx')}><DownloadSimple size={15} className="mr-1" />Excel</Button></div></div>{reconciliation.length === 0 && <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-slate-400">No reconciliation exceptions. Everything is matched.</div>}{pagedReconciliation.map((x) => <div key={x.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap items-center justify-between gap-3"><div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><p className="font-heading font-bold">{x.student_name}</p><span className={`text-xs font-bold px-2 py-1 rounded-full ${recClass(x.reconciliation_status)}`}>{recLabel(x.reconciliation_status)}</span></div><p className="text-xs text-slate-500 mt-1">{x.student_reference_id} · {x.transaction_id}</p><p className="text-sm mt-2">{inr(x.amount)} · {modeLabel(x.method)} · {fmtDate(x.payment_date)} {x.reference ? `· ${x.reference}` : ''}</p><p className="text-xs text-slate-500 mt-1">{x.reconciliation_status === 'pending_parent_confirmation' ? 'Legacy/school-recorded item awaiting review.' : x.reconciliation_status === 'pending_school_confirmation' ? 'Reported by parent; school confirmation required.' : x.reconciliation_status === 'pending_clearance' ? 'Cheque recorded; confirm only after clearance.' : 'Mismatch flagged; verify against school records before deciding.'}</p></div><div className="flex flex-wrap gap-2"><Button onClick={() => confirmSchoolPayment(x)} className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white">{x.reconciliation_status === 'pending_clearance' ? 'Mark Cleared & Reconcile' : 'Confirm & Reconcile'}</Button><Button variant="outline" onClick={() => rejectSchoolPayment(x)} className="rounded-full border-red-200 text-status-red hover:bg-red-50">Reject</Button></div></div>)}<Pager page={recPage} setPage={setRecPage} total={reconciliation.length} /></div>}

      <Dialog open={statementOpen} onOpenChange={setStatementOpen}><DialogContent data-hide-ask-edujac="true" className="rounded-2xl max-w-4xl max-h-[85vh] overflow-y-auto"><DialogHeader><DialogTitle>Student Fee Ledger</DialogTitle></DialogHeader>{statement && <><div className="flex justify-end gap-2"><Button size="sm" variant="outline" onClick={() => downloadFile(`/school/fees/students/${statement.student.id}/statement/export`, { format: 'pdf', academic_year_id: academicYearId || undefined }, `${statement.student.student_reference_id || 'student'}_fee_statement.pdf`)}><DownloadSimple size={15} className="mr-1" />PDF</Button><Button size="sm" variant="outline" onClick={() => downloadFile(`/school/fees/students/${statement.student.id}/statement/export`, { format: 'xlsx', academic_year_id: academicYearId || undefined }, `${statement.student.student_reference_id || 'student'}_fee_statement.xlsx`)}><DownloadSimple size={15} className="mr-1" />Excel</Button></div><div className="bg-[#F4F5F7] rounded-xl p-4"><p className="font-heading font-bold text-lg">{statement.student.name}</p><p className="text-xs text-slate-500">{statement.student.student_reference_id} · {statement.student.class_name}</p><p className="mt-2 text-sm">Closing balance: <span className="font-bold">{inr(statement.closing_balance)}</span></p></div><div className="overflow-x-auto"><table className="w-full text-sm"><thead className="text-xs uppercase text-slate-500"><tr><th className="py-2 text-left">Date</th><th className="py-2 text-left">Particulars</th><th className="py-2 text-left">Reference</th><th className="py-2 text-right">Debit</th><th className="py-2 text-right">Credit</th><th className="py-2 text-right">Balance</th></tr></thead><tbody>{statement.entries.map((e,i) => <tr key={`${e.type}-${i}`} className="border-t"><td className="py-2">{fmtDate(e.date)}</td><td className="py-2">{e.description}</td><td className="py-2 font-mono text-xs">{e.reference}</td><td className="py-2 text-right">{e.debit ? inr(e.debit) : '—'}</td><td className="py-2 text-right text-status-green">{e.credit ? inr(e.credit) : '—'}</td><td className="py-2 text-right font-bold">{inr(e.balance)}</td></tr>)}</tbody></table></div></>}</DialogContent></Dialog>

      <Dialog open={!!editInv} onOpenChange={(o) => !o && setEditInv(null)}><DialogContent className="rounded-2xl"><DialogHeader><DialogTitle>Edit invoice</DialogTitle></DialogHeader>{editInv && <form onSubmit={saveEditInvoice} className="space-y-4"><div><Label>Title</Label><Input className="mt-1.5" value={editInv.title} onChange={(e) => setEditInv({ ...editInv, title: e.target.value })} /></div><div><Label>Amount</Label><Input type="number" min="1" className="mt-1.5" value={editInv.amount} onChange={(e) => setEditInv({ ...editInv, amount: e.target.value })} /></div><div><Label>Due date</Label><Input type="date" className="mt-1.5" value={editInv.due_date} onChange={(e) => setEditInv({ ...editInv, due_date: e.target.value })} /></div><Button type="submit" className="w-full rounded-full bg-brand-coral hover:bg-brand-coralDark text-white">Save changes</Button></form>}</DialogContent></Dialog>

      <AlertDialog open={!!delInv} onOpenChange={(o) => !o && setDelInv(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Cancel this invoice?</AlertDialogTitle><AlertDialogDescription>The invoice will not be deleted. It will be marked cancelled to preserve the financial audit trail. Invoices with confirmed payments cannot be cancelled.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Keep invoice</AlertDialogCancel><AlertDialogAction onClick={confirmCancelInvoice}>Cancel invoice</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
    </div>
  );
}

function SchoolProfileTab() {
  const [form, setForm] = useState(null);
  const [razorpay, setRazorpay] = useState({ key_id: '', key_secret: '', webhook_secret: '', key_secret_configured: false, webhook_secret_configured: false });
  const [gatewaySaving, setGatewaySaving] = useState(false);
  const [profileSecurityToken, setProfileSecurityToken] = useState('');
  const [securityOpen, setSecurityOpen] = useState(false);
  const [securityStage, setSecurityStage] = useState('password');
  const [securityPassword, setSecurityPassword] = useState('');
  const [securityOtp, setSecurityOtp] = useState('');
  const [securityChallenge, setSecurityChallenge] = useState(null);
  const [securityBusy, setSecurityBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [assetUploading, setAssetUploading] = useState("");
  const [logoPreview, setLogoPreview] = useState("");

  useEffect(() => {
    api.get('/school/profile')
      .then(({ data }) => setForm(data))
      .catch((e) => toast.error(formatErr(e, 'Could not load school profile')));
  }, []);

  const secureHeaders = profileSecurityToken ? { 'X-Profile-Security-Token': profileSecurityToken } : {};

  useEffect(() => {
    if (!profileSecurityToken) return undefined;
    api.get('/school/payment-gateway-settings', { headers: { 'X-Profile-Security-Token': profileSecurityToken } })
      .then(({ data }) => setRazorpay({ ...data, key_secret: '', webhook_secret: '' }))
      .catch((e) => { setProfileSecurityToken(''); toast.error(formatErr(e, 'Profile security session expired')); });
    const timer = window.setTimeout(() => { setProfileSecurityToken(''); toast.info('Sensitive profile settings have been locked'); }, 10 * 60 * 1000);
    return () => window.clearTimeout(timer);
  }, [profileSecurityToken]);

  const openSecurity = () => {
    setSecurityStage('password'); setSecurityPassword(''); setSecurityOtp(''); setSecurityChallenge(null); setSecurityOpen(true);
  };

  const requestSecurityOtp = async (e) => {
    e.preventDefault(); setSecurityBusy(true);
    try {
      const { data } = await api.post('/school/profile-security/request-otp', { password: securityPassword });
      setSecurityChallenge(data); setSecurityPassword(''); setSecurityStage('otp');
      toast.success(data.development_otp ? 'Local test code is displayed in the verification window' : `Security code sent to ${data.masked_email}`);
    } catch (e) { toast.error(formatErr(e, 'Verification failed')); }
    finally { setSecurityBusy(false); }
  };

  const verifySecurityOtp = async (e) => {
    e.preventDefault(); setSecurityBusy(true);
    try {
      const { data } = await api.post('/school/profile-security/verify-otp', { challenge_id: securityChallenge.challenge_id, otp: securityOtp });
      setProfileSecurityToken(data.profile_security_token); setSecurityOpen(false); setSecurityOtp('');
      toast.success('Profile unlocked securely for 10 minutes');
    } catch (e) { toast.error(formatErr(e, 'Incorrect or expired security code')); }
    finally { setSecurityBusy(false); }
  };

  const saveRazorpay = async () => {
    if (!razorpay.key_id?.trim()) {
      toast.error('Enter the Razorpay Key ID');
      return;
    }
    setGatewaySaving(true);
    try {
      const { data } = await api.patch('/school/payment-gateway-settings', {
        key_id: razorpay.key_id.trim(),
        key_secret: razorpay.key_secret || null,
        webhook_secret: razorpay.webhook_secret || null,
      }, { headers: secureHeaders });
      setRazorpay({ ...data, key_secret: '', webhook_secret: '' });
      toast.success('Razorpay settings saved securely');
    } catch (e) { toast.error(formatErr(e, 'Could not save Razorpay settings')); }
    finally { setGatewaySaving(false); }
  };

  useEffect(() => {
    let objectUrl = "";
    let cancelled = false;
    const path = form?.logo_url;
    if (!path) {
      setLogoPreview("");
      return undefined;
    }
    if (/^https?:\/\//i.test(path)) {
      setLogoPreview(path);
      return undefined;
    }
    api.get('/files/download', { params: { path }, responseType: 'blob' })
      .then(({ data }) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(data);
        setLogoPreview(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setLogoPreview("");
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [form?.logo_url]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch('/school/profile', form, { headers: secureHeaders });
      setForm(data);
      toast.success('School profile saved');
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const allowed = /\.(png|jpe?g|webp)$/i.test(file.name);
    if (!allowed) {
      toast.error('Use PNG, JPG, JPEG or WebP for the school logo');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('School logo must be 5 MB or smaller');
      return;
    }
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data: uploaded } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { data: updated } = await api.patch('/school/profile', { logo_url: uploaded.path }, { headers: secureHeaders });
      setForm(updated);
      toast.success('School logo uploaded and linked to this school');
    } catch (e) {
      toast.error(formatErr(e, 'Logo upload failed'));
    } finally {
      setLogoUploading(false);
    }
  };

  const removeLogo = async () => {
    setLogoUploading(true);
    try {
      const { data } = await api.patch('/school/profile', { logo_url: '' }, { headers: secureHeaders });
      setForm(data);
      setLogoPreview('');
      toast.success('School logo removed from profile');
    } catch (e) {
      toast.error(formatErr(e, 'Could not remove logo'));
    } finally {
      setLogoUploading(false);
    }
  };

  const uploadProfileAsset = async (key, label, file) => {
    if (!file) return;
    if (!/\.(png|jpe?g|webp)$/i.test(file.name) || file.size > 5 * 1024 * 1024) {
      toast.error(`${label} must be a PNG, JPG or WebP image up to 5 MB`);
      return;
    }
    setAssetUploading(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data: uploaded } = await api.post('/files/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { data } = await api.patch('/school/profile', { [key]: uploaded.path }, { headers: secureHeaders });
      setForm(data);
      toast.success(`${label} uploaded`);
    } catch (e) { toast.error(formatErr(e, `${label} upload failed`)); }
    finally { setAssetUploading(""); }
  };

  if (!form) return <p className="text-sm text-slate-400">Loading school profile…</p>;
  const field = (key, label, placeholder = '') => <div><Label>{label}</Label><Input className="mt-1.5" value={form[key] || ''} placeholder={placeholder} onChange={(e) => setForm({ ...form, [key]: e.target.value })} /></div>;

  return <div data-testid="school-profile-tab">
    <div className="mb-5">
      <h1 className="font-heading text-2xl font-extrabold">School Profile</h1>
      <p className="text-sm text-slate-500 mt-1">Single source of truth for receipts, invoices, reports and official school documents.</p>
    </div>
    <div className={`mb-4 rounded-2xl border p-4 flex flex-wrap items-center justify-between gap-3 ${profileSecurityToken ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
      <div><p className="font-bold">{profileSecurityToken ? 'Sensitive profile settings unlocked' : 'Sensitive profile settings are locked'}</p><p className="text-xs text-slate-500 mt-1">{profileSecurityToken ? 'You can edit this profile for 10 minutes. Changes are recorded in the security audit log.' : 'Confirm the admin password and email security code before editing profile, bank or gateway information.'}</p></div>
      {profileSecurityToken ? <Button type="button" variant="outline" onClick={() => setProfileSecurityToken('')}>Lock now</Button> : <Button type="button" onClick={openSecurity} className="bg-brand-coral hover:bg-brand-coralDark text-white">Unlock Profile</Button>}
    </div>
    <Dialog open={securityOpen} onOpenChange={setSecurityOpen}><DialogContent className="rounded-2xl max-w-md"><DialogHeader><DialogTitle>Secure Profile Verification</DialogTitle></DialogHeader>
      {securityStage === 'password' ? <form onSubmit={requestSecurityOtp} className="space-y-4"><p className="text-sm text-slate-500">Enter your current admin password. We will then send a one-time code to your registered email.</p><div><Label>Admin password</Label><Input autoFocus required type="password" autoComplete="current-password" className="mt-1.5" value={securityPassword} onChange={(e) => setSecurityPassword(e.target.value)} /></div><Button disabled={securityBusy} className="w-full bg-brand-coral hover:bg-brand-coralDark text-white">{securityBusy ? 'Verifying…' : 'Verify Password & Send Code'}</Button></form>
      : <form onSubmit={verifySecurityOtp} className="space-y-4"><p className="text-sm text-slate-500">{securityChallenge?.development_otp ? 'Email is unavailable in local development. Use the local test code below.' : <>Enter the 6-digit code sent to <b>{securityChallenge?.masked_email}</b>.</>} It expires in 5 minutes.</p>{securityChallenge?.development_otp && <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-center"><p className="text-xs font-bold text-amber-700">LOCAL TEST OTP — NEVER SHOWN IN PRODUCTION</p><p className="font-mono text-2xl font-extrabold tracking-[0.35em] mt-1">{securityChallenge.development_otp}</p></div>}<div><Label>Email security code</Label><Input autoFocus required inputMode="numeric" pattern="[0-9]{6}" maxLength={6} className="mt-1.5 text-center text-xl tracking-[0.4em]" value={securityOtp} onChange={(e) => setSecurityOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} /></div><Button disabled={securityBusy || securityOtp.length !== 6} className="w-full bg-brand-coral hover:bg-brand-coralDark text-white">{securityBusy ? 'Checking…' : 'Unlock Profile for 10 Minutes'}</Button><Button type="button" variant="ghost" className="w-full" onClick={() => { setSecurityStage('password'); setSecurityOtp(''); }}>Start again</Button></form>}
    </DialogContent></Dialog>
    <form onSubmit={save} className={`bg-white border border-slate-200 rounded-2xl p-5 space-y-5 transition-opacity ${profileSecurityToken ? '' : 'pointer-events-none opacity-60 select-none'}`}>
      <div className="grid md:grid-cols-3 gap-4">{field('name','School name')}{field('branch_name','Branch name')}{field('school_code','School code')}</div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-36 h-24 rounded-xl border border-slate-200 bg-white flex items-center justify-center overflow-hidden shrink-0">
            {logoPreview ? <img src={logoPreview} alt="School logo" className="w-full h-full object-contain p-2" /> : <div className="text-center px-3"><p className="text-xs font-bold text-slate-500">SCHOOL LOGO</p><p className="text-[11px] text-slate-400 mt-1">No logo uploaded</p></div>}
          </div>
          <div className="flex-1">
            <Label>School / Branch logo</Label>
            <p className="text-xs text-slate-500 mt-1">Upload once here. EDUJAC will use this exact school logo on its fee receipts and official documents. The original image is preserved and fitted without stretching or cropping.</p>
            <div className="flex flex-wrap gap-2 mt-3">
              <label htmlFor="school-logo-upload" className={`inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-bold cursor-pointer border border-slate-300 bg-white hover:bg-slate-100 ${logoUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                {logoUploading ? 'Uploading…' : (form.logo_url ? 'Replace Logo' : 'Upload Logo')}
              </label>
              <input id="school-logo-upload" type="file" accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp" className="hidden" onChange={uploadLogo} disabled={logoUploading} />
              {form.logo_url && <Button type="button" variant="outline" className="rounded-full" disabled={logoUploading} onClick={removeLogo}>Remove Logo</Button>}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">PNG, JPG, JPEG or WebP · maximum 5 MB · recommended transparent PNG or high-quality square/wide logo.</p>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        {[
          ['stamp_url', 'Digital school stamp'],
          ['signature_url', 'Authorized signature'],
          ['payment_qr_url', 'Payment QR code'],
        ].map(([key, label]) => <div key={key} className="bg-white border border-slate-200 rounded-xl p-3">
          <Label>{label}</Label>
          <p className="text-[11px] text-slate-500 mt-1">{form[key] ? 'Uploaded and linked to this branch' : 'No image uploaded'}</p>
          <div className="flex gap-2 mt-3">
            <label className={`inline-flex rounded-full border px-3 py-1.5 text-xs font-bold cursor-pointer ${assetUploading ? 'opacity-50 pointer-events-none' : ''}`}>
              {assetUploading === key ? 'Uploading…' : (form[key] ? 'Replace' : 'Upload')}
              <input type="file" accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadProfileAsset(key, label, f); }} />
            </label>
            {form[key] && <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => setForm({ ...form, [key]: '' })}>Remove</Button>}
          </div>
        </div>)}
      </div>

      <div className="grid md:grid-cols-2 gap-4">{field('address_line1','Address line 1')}{field('address_line2','Address line 2')}</div>
      <div className="grid md:grid-cols-4 gap-4">{field('area','Area / Locality')}{field('city','City')}{field('district','District')}{field('state','State')}</div>
      <div className="grid md:grid-cols-3 gap-4">{field('pin_code','PIN code')}{field('country','Country')}{field('website','Website')}</div>
      <div className="grid md:grid-cols-3 gap-4">{field('phone','Primary phone')}{field('alternate_phone','Alternate phone')}{field('email','School email')}</div>
      <div className="grid md:grid-cols-4 gap-4">{field('principal_name','Principal / Head')}{field('board','Board')}{field('affiliation_no','Affiliation number')}{field('registration_no','Registration number')}</div>
      <div className="grid md:grid-cols-3 gap-4">{field('gst_no','GST / Tax number (optional)')}{field('authorized_signatory_name','Authorized signatory name')}{field('authorized_signatory_label','Authorized signatory designation','Principal / Accounts Officer')}</div>
      <div className="rounded-2xl border border-slate-200 p-4 space-y-4">
        <div><h3 className="font-heading font-bold">Payment collection settings</h3><p className="text-xs text-slate-500">These branch-specific details use the existing Fee Accounts invoices, ledger and receipts.</p></div>
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm font-semibold"><Checkbox checked={!!form.manual_payment_enabled} onCheckedChange={(v) => setForm({ ...form, manual_payment_enabled: !!v })} />Enable static QR / bank transfer</label>
          <label className="flex items-center gap-2 text-sm font-semibold"><Checkbox checked={!!form.online_payment_enabled} onCheckedChange={(v) => setForm({ ...form, online_payment_enabled: !!v })} />Enable gateway payment</label>
        </div>
        <div className="grid md:grid-cols-3 gap-4">{field('payment_upi_id','UPI ID')}{field('payment_account_holder','Account holder')}{field('payment_bank_name','Bank name')}</div>
        <div className="grid md:grid-cols-3 gap-4">{field('payment_account_number','Account number')}{field('payment_ifsc','IFSC')}{field('payment_bank_branch','Bank branch')}</div>
        <div><Label>Gateway provider</Label><Select value={form.payment_provider || 'stripe'} onValueChange={(v) => setForm({ ...form, payment_provider: v })}><SelectTrigger className="mt-1.5 max-w-xs"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="stripe">Stripe</SelectItem><SelectItem value="razorpay">Razorpay</SelectItem></SelectContent></Select></div>
        {form.payment_provider === 'razorpay' && <div className="rounded-xl border border-blue-200 bg-blue-50/50 p-4 space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div><h4 className="font-bold">Razorpay API setup</h4><p className="text-xs text-slate-500 mt-1">Enter this branch's credentials from Razorpay Dashboard → Account &amp; Settings → API Keys. Secrets are encrypted and are never shown again.</p></div>
            {razorpay.mode && <span className={`text-xs font-bold rounded-full px-2.5 py-1 ${razorpay.mode === 'live' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{razorpay.mode === 'live' ? 'LIVE MODE' : 'TEST MODE'}</span>}
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            <div><Label>Razorpay Key ID</Label><Input className="mt-1.5" autoComplete="off" value={razorpay.key_id || ''} onChange={(e) => setRazorpay({ ...razorpay, key_id: e.target.value })} placeholder="rzp_test_… or rzp_live_…" /></div>
            <div><Label>Key Secret {razorpay.key_secret_configured && <span className="text-green-600 text-xs">· Configured</span>}</Label><Input className="mt-1.5" type="password" autoComplete="new-password" value={razorpay.key_secret || ''} onChange={(e) => setRazorpay({ ...razorpay, key_secret: e.target.value })} placeholder={razorpay.key_secret_configured ? 'Leave blank to keep existing' : 'Required'} /></div>
            <div><Label>Webhook Secret {razorpay.webhook_secret_configured && <span className="text-green-600 text-xs">· Configured</span>}</Label><Input className="mt-1.5" type="password" autoComplete="new-password" value={razorpay.webhook_secret || ''} onChange={(e) => setRazorpay({ ...razorpay, webhook_secret: e.target.value })} placeholder={razorpay.webhook_secret_configured ? 'Leave blank to keep existing' : 'Required for webhooks'} /></div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3"><p className="text-xs text-slate-500">Webhook URL: <span className="font-mono">{`${window.location.origin}/api/webhook/razorpay`}</span></p><Button type="button" variant="outline" disabled={gatewaySaving} onClick={saveRazorpay}>{gatewaySaving ? 'Saving…' : 'Save Razorpay Setup'}</Button></div>
        </div>}
        <div><Label>Payment instructions</Label><Textarea className="mt-1.5" value={form.payment_instructions || ''} onChange={(e) => setForm({ ...form, payment_instructions: e.target.value })} placeholder="Enter the UTR after payment. Fees update after school verification." /></div>
      </div>
      <div><Label>Document header note</Label><Input className="mt-1.5" value={form.document_header_note || ''} onChange={(e) => setForm({ ...form, document_header_note: e.target.value })} placeholder="Optional school trust / recognition line" /></div>
      <div><Label>Receipt footer</Label><Textarea className="mt-1.5" value={form.receipt_footer || ''} onChange={(e) => setForm({ ...form, receipt_footer: e.target.value })} placeholder="This receipt is valid for the payment recorded above." /></div>
      <div><Label>Invoice footer</Label><Textarea className="mt-1.5" value={form.invoice_footer || ''} onChange={(e) => setForm({ ...form, invoice_footer: e.target.value })} placeholder="Payment terms / bank instructions / notes shown on fee invoices" /></div>
      <div className="flex justify-end"><Button disabled={saving || logoUploading || !!assetUploading} type="submit" className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold">{saving ? 'Saving…' : 'Save School Profile'}</Button></div>
    </form>
  </div>;
}
